import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  Alert,
  Button,
  Card,
  Col,
  Checkbox,
  Dropdown,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import { ContactsOutlined, EyeOutlined, PhoneOutlined, PlusOutlined, RobotOutlined } from '@ant-design/icons';
import {
  api,
  createWorkListSaveJob,
  exportWorkListExcel,
  fetchWorkListDetail,
  fetchWorkListSaveJob,
  fetchWorkLists,
  refreshWorkList,
  updateWorkListItem,
  updateWorkListItemEnrichment
} from './api';
import { formatCvrNumber, formatDateTimeFull, formatDateTimeShort } from './constants';
import {
  CompanyAiAddressContactCandidate,
  CompanyContactLookup,
  CompanyAiSummary,
  CompanyInsuranceRelevanceEvent,
  CompanyInsuranceRelevanceEvents,
  CompanyPropertyChangeEvent,
  CompanyPropertyRegisterChanges,
  CompanyPropertyRegisterLookup,
  CompanySearchResult,
  CvrSummary,
  Label,
  WorkListItem,
  WorkListSaveJob,
  WorkListSummary
} from './types';
import LeadDetailPage from './LeadDetailPage';
import { useSearchParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

type Props = {
  canManage?: boolean;
  initialWorkListId?: string | null;
  onLeadCreated?: () => void | Promise<void>;
  onWorkListCreated?: (workListId: string) => void;
};

type WorkListDetailPayload = Awaited<ReturnType<typeof fetchWorkListDetail>>;

type CachedWorkListPage = {
  workList: WorkListSummary;
  results: CompanySearchResult[];
  total: number;
  page: number;
  limit: number;
};

type WorkListChangeFilter = 'ALL' | 'NEW' | 'UPDATED' | 'CHANGED';
type WorkListBooleanFilter = 'ALL' | 'YES' | 'NO';

type WorkListToolbarFilters = {
  q: string;
  changeFilter: WorkListChangeFilter;
  emailSent: WorkListBooleanFilter;
  alreadyCustomer: WorkListBooleanFilter;
  ignored: WorkListBooleanFilter;
};

type SearchFormValues = {
  name?: string;
  cvr?: string;
  status?: string;
  branche?: string;
  postal?: string;
  municipality?: string;
  includeExistingLeads?: boolean;
  minEmployees?: number | null;
  maxEmployees?: number | null;
  limit?: number;
  saveAsList?: boolean;
  workListName?: string;
};

type NormalizedSearchValues = {
  name: string;
  cvr: string;
  status: string;
  branche: string;
  postal: string;
  municipality: string;
  includeExistingLeads: boolean;
  minEmployees: number | null;
  maxEmployees: number | null;
  limit: number;
};

const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 1000;
const DEFAULT_LABEL_COLOR = '#d3d3d3';

const DEFAULT_WORK_LIST_FILTERS: WorkListToolbarFilters = {
  q: '',
  changeFilter: 'ALL',
  emailSent: 'ALL',
  alreadyCustomer: 'ALL',
  ignored: 'ALL'
};

function workListPageCacheKey(
  workListId: string,
  page: number,
  limit: number,
  filters: WorkListToolbarFilters
) {
  return [
    workListId,
    page,
    limit,
    filters.q.trim().toLocaleLowerCase('da-DK'),
    filters.changeFilter,
    filters.emailSent,
    filters.alreadyCustomer,
    filters.ignored
  ].join(':');
}

type CompanyUser = {
  id: string;
  full_name: string | null;
  email: string | null;
  role?: string | null;
};

function onlyDigits(value: string | null | undefined) {
  if (!value) {
    return '';
  }
  return value.replace(/\D+/g, '');
}

function normalizeNullableText(value: unknown) {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  return text ? text : null;
}

function normalizeAssignedLabels(value: unknown): Label[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const raw = item as Partial<Label>;
      const id = typeof raw.id === 'string' ? raw.id.trim() : '';
      const name = typeof raw.name === 'string' ? raw.name.trim() : '';
      if (!id || !name) {
        return null;
      }
      return {
        id,
        name,
        color: typeof raw.color === 'string' && raw.color.trim() ? raw.color : DEFAULT_LABEL_COLOR,
        created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date(0).toISOString(),
        hideInSearch: Boolean(raw.hideInSearch),
        leadCount: Number(raw.leadCount ?? 0)
      } as Label;
    })
    .filter((item): item is Label => Boolean(item));
}

function normalizePageSize(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.floor(parsed), MAX_PAGE_SIZE);
}

function normalizeEmployeeBound(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const floored = Math.floor(parsed);
  if (floored < 0) {
    return null;
  }
  return floored;
}

function normalizeSearchValues(values: SearchFormValues): NormalizedSearchValues {
  const brancheRaw = values.branche;
  return {
    name: values.name?.trim() ?? '',
    cvr: values.cvr?.trim() ?? '',
    status: values.status?.trim() ?? '',
    branche: typeof brancheRaw === 'string' ? brancheRaw.trim() : '',
    postal: values.postal?.trim() ?? '',
    municipality: values.municipality?.trim() ?? '',
    includeExistingLeads: Boolean(values.includeExistingLeads),
    minEmployees: normalizeEmployeeBound(values.minEmployees),
    maxEmployees: normalizeEmployeeBound(values.maxEmployees),
    limit: normalizePageSize(values.limit)
  };
}

function hasSearchCriteria(values: NormalizedSearchValues) {
  return Boolean(
    values.name ||
      values.cvr ||
      values.status ||
      values.branche ||
      values.postal ||
      values.municipality
  );
}

function getValuesFromParams(params: URLSearchParams): SearchFormValues {
  return {
    name: params.get('name') || undefined,
    cvr: params.get('cvr') || undefined,
    status: params.get('status') || undefined,
    branche: params.get('branche') || undefined,
    postal: params.get('postal') || undefined,
    municipality: params.get('municipality') || undefined,
    includeExistingLeads: params.get('includeExistingLeads') === 'true',
    minEmployees: (() => {
      const parsed = normalizeEmployeeBound(params.get('minEmployees'));
      return parsed === null ? undefined : parsed;
    })(),
    maxEmployees: (() => {
      const parsed = normalizeEmployeeBound(params.get('maxEmployees'));
      return parsed === null || parsed >= 999 ? undefined : parsed;
    })(),
    limit: params.get('limit') ? normalizePageSize(params.get('limit')) : undefined
  };
}

function getValuesFromCriteria(criteria: Record<string, unknown> | null | undefined): SearchFormValues {
  const text = (key: string) => {
    const value = criteria?.[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  };
  const numeric = (key: string) => {
    const value = criteria?.[key];
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  return {
    name: text('name'),
    cvr: text('cvr'),
    status: text('status'),
    branche: text('branche'),
    postal: text('postal'),
    municipality: text('municipality') ?? text('kommune'),
    includeExistingLeads: Boolean(criteria?.includeExistingLeads),
    minEmployees: numeric('minEmployees'),
    maxEmployees: numeric('maxEmployees'),
    limit: numeric('limit')
  };
}

function buildQueryParams(values: NormalizedSearchValues) {
  const params = new URLSearchParams();
  if (values.name) {
    params.set('name', values.name);
  }
  if (values.cvr) {
    params.set('cvr', values.cvr);
  }
  if (values.status) {
    params.set('status', values.status);
  }
  if (values.branche) {
    params.set('branche', values.branche);
  }
  if (values.postal) {
    params.set('postal', values.postal);
  }
  if (values.municipality) {
    params.set('municipality', values.municipality);
  }
  if (values.minEmployees !== null) {
    params.set('minEmployees', String(values.minEmployees));
  }
  if (values.maxEmployees !== null) {
    params.set('maxEmployees', String(values.maxEmployees));
  }
  if (values.includeExistingLeads) {
    params.set('includeExistingLeads', 'true');
  }
  if (values.limit && values.limit !== DEFAULT_PAGE_SIZE) {
    params.set('limit', String(values.limit));
  }
  return params;
}

function getPageFromParams(params: URLSearchParams) {
  const raw = params.get('page');
  if (!raw) {
    return 1;
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

function applyPageParam(params: URLSearchParams, page: number) {
  if (page > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }
}

function buildAddress(summary: CvrSummary | null) {
  if (!summary) {
    return '';
  }
  const segments: string[] = [];
  const street: string[] = [];
  if (summary.vejnavn) {
    street.push(summary.vejnavn.trim());
  }
  const from = summary.husnummer_fra?.trim();
  const to = summary.husnummer_til?.trim();
  if (from && to && from !== to) {
    street.push(`${from}-${to}`);
  } else if (from) {
    street.push(from);
  } else if (to) {
    street.push(to);
  }
  if (summary.etage) {
    street.push(`${summary.etage}.`);
  }
  if (summary.doer) {
    street.push(summary.doer);
  }
  if (street.length) {
    segments.push(street.join(' ').replace(/\s+/g, ' ').trim());
  }
  if (summary.supplerende_bynavn) {
    segments.push(summary.supplerende_bynavn.trim());
  }
  const postal: string[] = [];
  if (summary.postnummer) {
    postal.push(summary.postnummer.trim());
  }
  if (summary.postdistrikt) {
    postal.push(summary.postdistrikt.trim());
  }
  if (postal.length) {
    segments.push(postal.join(' ').trim());
  }
  if (summary.kommunenavn) {
    segments.push(summary.kommunenavn.trim());
  }
  return segments.join(', ');
}

function formatUpdated(value: string | null | undefined) {
  if (!value) {
    return '—';
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('da-DK', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  } catch (error) {
    console.error('Failed to format timestamp', error);
    return value;
  }
}

function formatDateOnly(value: string | null | undefined) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium' }).format(date);
}

function propertyChangeLabel(changeType: CompanyPropertyChangeEvent['changeType']) {
  switch (changeType) {
    case 'new':
      return 'Ny';
    case 'changed':
      return 'Ændret';
    case 'missing':
      return 'Mangler';
    case 'reappeared':
      return 'Genfundet';
    default:
      return changeType;
  }
}

function propertyChangeColor(changeType: CompanyPropertyChangeEvent['changeType']) {
  switch (changeType) {
    case 'new':
      return 'green';
    case 'changed':
      return 'blue';
    case 'missing':
      return 'orange';
    case 'reappeared':
      return 'purple';
    default:
      return 'default';
  }
}

function insuranceSeverityColor(severity: CompanyInsuranceRelevanceEvent['severity']) {
  switch (severity) {
    case 'CRITICAL':
      return 'red';
    case 'HIGH':
      return 'volcano';
    case 'MEDIUM':
      return 'orange';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

function insuranceSeverityLabel(severity: CompanyInsuranceRelevanceEvent['severity']) {
  switch (severity) {
    case 'CRITICAL':
      return 'Kritisk';
    case 'HIGH':
      return 'Høj';
    case 'MEDIUM':
      return 'Middel';
    case 'LOW':
      return 'Lav';
    default:
      return severity;
  }
}

function isConstructionRelatedInsuranceEvent(event: CompanyInsuranceRelevanceEvent) {
  return [
    'NEW_BUILDING',
    'BUILDING_REMOVED',
    'BUILDING_STATUS_CHANGED',
    'BUILDING_USAGE_CHANGED',
    'BUILDING_AREA_CHANGED',
    'CONSTRUCTION_STARTED',
    'CONSTRUCTION_UPDATED'
  ].includes(event.eventType);
}

function statusColor(status: string | null | undefined) {
  if (!status) {
    return 'default';
  }
  const normalized = status.toLowerCase();
  if (normalized.includes('aktiv')) {
    return 'green';
  }
  if (normalized.includes('under opløsning') || normalized.includes('opløsning')) {
    return 'orange';
  }
  if (normalized.includes('opløst') || normalized.includes('konkurs')) {
    return 'red';
  }
  return 'blue';
}

function formatEmployeeCountLabel(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  if (value === 0) {
    return '0 (ingen ansatte registreret)';
  }
  return String(value);
}

function buildSummaryPreview(record: CompanySearchResult): CvrSummary {
  return {
    cvrnummer: record.cvrnummer,
    navn: record.navn,
    virksomhed_status: record.virksomhed_status,
    branchekode: null,
    branchetekst: record.branchetekst,
    opdateret_tidspunkt: record.opdateret_tidspunkt,
    ansatte_aar: (record as any).ansatte_aar ?? null,
    ansatte_datotil: (record as any).ansatte_datotil ?? null,
    adresseringanvendelse: null,
    vejnavn: null,
    husnummer_fra: null,
    husnummer_til: null,
    etage: null,
    doer: null,
    postnummer: record.postnummer,
    postdistrikt: record.postdistrikt,
    supplerende_bynavn: null,
    kommunenavn: record.kommunenavn,
    telefon: record.telefon,
    email: record.email,
    co_navn: null
  };
}

function mapWorkListItemToCompanyResult(item: WorkListItem): CompanySearchResult {
  const row = item.row_data || {};
  const cvr = onlyDigits(String(row.cvrnummer ?? row.cvr ?? item.company_cvr ?? ''));
  const ansatteRaw = (row as any).ansatte_aar ?? (row as any).ansatteAar;
  const ansatte = ansatteRaw === null || ansatteRaw === undefined ? null : Number(ansatteRaw);
  const assignedLabels = normalizeAssignedLabels((row as any).labels);

  return {
    ...(row as Record<string, unknown>),
    id: item.id,
    cvrnummer: cvr,
    navn: normalizeNullableText((row as any).navn ?? item.company_name),
    virksomhed_status: normalizeNullableText((row as any).virksomhed_status ?? (row as any).status),
    branchetekst: normalizeNullableText((row as any).branchetekst ?? (row as any).branche),
    postnummer: normalizeNullableText((row as any).postnummer),
    postdistrikt: normalizeNullableText((row as any).postdistrikt),
    kommunenavn: normalizeNullableText((row as any).kommunenavn),
    opdateret_tidspunkt: normalizeNullableText((row as any).opdateret_tidspunkt),
    telefon: normalizeNullableText((row as any).telefon),
    email: normalizeNullableText((row as any).email),
    email_sendt: item.email_sendt,
    bemaerkninger: item.bemaerkninger,
    allerede_kunde: item.allerede_kunde,
    ignorer: item.ignorer,
    beriget_hjemmeside: item.beriget_hjemmeside,
    beriget_kontakt: item.beriget_kontakt,
    beriget_telefon: item.beriget_telefon,
    beriget_email: item.beriget_email,
    leadId: item.lead_id ?? ((row as any).leadId || null),
    hasVisibleLead: Boolean(item.lead_id ?? (row as any).hasVisibleLead),
    hasHiddenLead: Boolean((row as any).hasHiddenLead),
    hasCustomer: Boolean(item.allerede_kunde || (row as any).hasCustomer || (row as any).alreadyCustomer),
    hideInSearch: Boolean((row as any).hideInSearch),
    labels: assignedLabels,
    productionUnitCount: Number((row as any).productionUnitCount ?? (row as any).production_unit_count ?? 0),
    callAttemptCount: Number((row as any).callAttemptCount ?? (row as any).call_attempt_count ?? 0),
    lastCallAt: normalizeNullableText((row as any).lastCallAt ?? (row as any).last_call_at),
    ansatte_aar: Number.isFinite(ansatte) ? ansatte : null,
    ansatte_datotil: normalizeNullableText((row as any).ansatte_datotil ?? (row as any).ansatteDatotil)
  } as CompanySearchResult;
}

export default function CompanySearchPage({ canManage = false, initialWorkListId = null, onLeadCreated, onWorkListCreated }: Props) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [workLists, setWorkLists] = useState<WorkListSummary[]>([]);
  const [workListsLoading, setWorkListsLoading] = useState(false);
  const [activeWorkList, setActiveWorkList] = useState<WorkListSummary | null>(null);
  const [saveJob, setSaveJob] = useState<WorkListSaveJob | null>(null);
  const [labels, setLabels] = useState<Label[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [assigningLabelFor, setAssigningLabelFor] = useState<string | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<CvrSummary | null>(null);
  const [detailCvr, setDetailCvr] = useState<string | null>(null);
  const [propertyDetailLoading, setPropertyDetailLoading] = useState(false);
  const [propertyDetail, setPropertyDetail] = useState<CompanyPropertyRegisterLookup | null>(null);
  const [propertyDetailError, setPropertyDetailError] = useState<string | null>(null);
  const [propertyChangesLoading, setPropertyChangesLoading] = useState(false);
  const [propertyChanges, setPropertyChanges] = useState<CompanyPropertyRegisterChanges | null>(null);
  const [propertyChangesError, setPropertyChangesError] = useState<string | null>(null);
  const [insuranceEventsLoading, setInsuranceEventsLoading] = useState(false);
  const [insuranceEvents, setInsuranceEvents] = useState<CompanyInsuranceRelevanceEvents | null>(null);
  const [insuranceEventsError, setInsuranceEventsError] = useState<string | null>(null);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [aiModalLoading, setAiModalLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState<CompanyAiSummary | null>(null);
  const [aiSummaryCvr, setAiSummaryCvr] = useState<string | null>(null);
  const [aiAnalyzingFor, setAiAnalyzingFor] = useState<string | null>(null);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [contactModalLoading, setContactModalLoading] = useState(false);
  const [contactLookup, setContactLookup] = useState<CompanyContactLookup | null>(null);
  const [contactLookupCvr, setContactLookupCvr] = useState<string | null>(null);
  const [contactLoadingFor, setContactLoadingFor] = useState<string | null>(null);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [usersCompany, setUsersCompany] = useState<{ id: string; name: string | null; cvr: string } | null>(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [userForm] = Form.useForm();
  const [workListDraftValues, setWorkListDraftValues] = useState<
    Record<
      string,
      Partial<
        Pick<
          WorkListItem,
          'beriget_hjemmeside' | 'beriget_kontakt' | 'beriget_telefon' | 'beriget_email' | 'bemaerkninger'
        >
      >
    >
  >({});
  const [workListUpdatingFor, setWorkListUpdatingFor] = useState<string | null>(null);
  const [workListFilterDraft, setWorkListFilterDraft] = useState<WorkListToolbarFilters>(
    DEFAULT_WORK_LIST_FILTERS
  );
  const [appliedWorkListFilters, setAppliedWorkListFilters] = useState<WorkListToolbarFilters>(
    DEFAULT_WORK_LIST_FILTERS
  );
  const [workListRefreshing, setWorkListRefreshing] = useState(false);
  const [workListExporting, setWorkListExporting] = useState(false);
  const aiModalSections = useMemo(() => {
    const raw = aiSummary?.ai_summary_text;
    if (!raw) {
      return [] as string[];
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return [] as string[];
    }
    return trimmed
      .split(/\n{2,}/)
      .map((block: string) => block.trim())
      .filter(Boolean);
  }, [aiSummary?.ai_summary_text]);

  const currentUsersCompanyId = usersCompany?.id;

  const closeUsersModal = useCallback(() => {
    setUsersModalOpen(false);
    setUsersCompany(null);
    setCompanyUsers([]);
    userForm.resetFields();
  }, [userForm]);

  const loadCompanyUsers = useCallback(async () => {
    if (!usersCompany) return;
    setUsersLoading(true);
    try {
      const res = await api(`/api/companies/${encodeURIComponent(usersCompany.id)}/users`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente brugere');
      }
      const data = (await res.json()) as CompanyUser[];
      setCompanyUsers(data);
    } catch (error: any) {
      console.error('Failed to load company users', error);
      message.error(error?.message || 'Kunne ikke hente brugere');
    } finally {
      setUsersLoading(false);
    }
  }, [usersCompany]);

  useEffect(() => {
    if (usersModalOpen) {
      void loadCompanyUsers();
    }
  }, [usersModalOpen, loadCompanyUsers]);

  const handleCreateUser = useCallback(async () => {
    if (!usersCompany) return;
    try {
      const values = await userForm.validateFields();
      setCreateUserLoading(true);
      const payload = {
        full_name: values.full_name?.trim() || null,
        email: values.email?.trim() || null,
        role: values.role || null
      };
      const res = await api(`/api/companies/${encodeURIComponent(usersCompany.id)}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette bruger');
      }
      message.success('Bruger oprettet');
      userForm.resetFields();
      await loadCompanyUsers();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Create user failed', error);
      message.error(error?.message || 'Kunne ikke oprette bruger');
    } finally {
      setCreateUserLoading(false);
    }
  }, [usersCompany, userForm, loadCompanyUsers]);

  const aiModalGeneratedLabel = useMemo(() => {
    if (!aiSummary?.ai_summary_generated_at) {
      return null;
    }
    return formatDateTimeFull(aiSummary.ai_summary_generated_at);
  }, [aiSummary?.ai_summary_generated_at]);
  const aiAddressContacts = useMemo(() => {
    const candidates = aiSummary?.address_contact_candidates;
    if (!Array.isArray(candidates)) {
      return [] as CompanyAiAddressContactCandidate[];
    }
    return candidates.filter((candidate) => {
      if (!candidate) {
        return false;
      }
      const hasName = typeof candidate.navn === 'string' && candidate.navn.trim().length > 0;
      const hasEmail = typeof candidate.email === 'string' && candidate.email.trim().length > 0;
      const hasPhone = typeof candidate.telefon === 'string' && candidate.telefon.trim().length > 0;
      return hasName || hasEmail || hasPhone;
    });
  }, [aiSummary?.address_contact_candidates]);
  const [leadCreatingFor, setLeadCreatingFor] = useState<string | null>(null);
  const [quickCallTarget, setQuickCallTarget] = useState<string | null>(null);
  const [brancheOptions, setBrancheOptions] = useState<
    { code: string | null; text: string }[]
  >([]);
  const [brancheLoading, setBrancheLoading] = useState(false);
  const [form] = Form.useForm();
  const saveAsListChecked = Form.useWatch('saveAsList', form);
  const [leadDetailVisible, setLeadDetailVisible] = useState(false);
  const [leadDetailId, setLeadDetailId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const lastExecutedQueryRef = useRef<string>('');
  const workListPageCacheRef = useRef(new Map<string, CachedWorkListPage>());
  const workListPrefetchingRef = useRef(new Set<string>());
  const workListCacheGenerationRef = useRef(new Map<string, number>());
  const workListViewRequestRef = useRef(0);
  const activeWorkListIdRef = useRef<string | null>(null);
  const currentWorkListViewRef = useRef<{
    workListId: string;
    filters: WorkListToolbarFilters;
    page: number;
    limit: number;
  } | null>(null);
  const workListNavigationGenerationRef = useRef(0);
  const saveJobAutoOpenIntentRef = useRef<{ jobId: string; generation: number } | null>(null);
  const workListRefreshGenerationRef = useRef(0);
  const workListEnrichmentQueueRef = useRef(new Map<string, Promise<void>>());
  const workListEnrichmentGenerationRef = useRef(new Map<string, number>());
  const searchParamsKey = searchParams.toString();
  const paramsValues = useMemo(
    () => getValuesFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );
  const leadDetailLocationState = useMemo(
    () => (searchParamsKey ? { fromCompanySearch: searchParamsKey } : undefined),
    [searchParamsKey]
  );
  const [currentPage, setCurrentPage] = useState(() =>
    getPageFromParams(new URLSearchParams(searchParamsKey))
  );
  const [pageSize, setPageSize] = useState(() => paramsValues.limit ?? DEFAULT_PAGE_SIZE);
  const pageFromParams = useMemo(
    () => getPageFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );

  const loadWorkLists = useCallback(async () => {
    setWorkListsLoading(true);
    try {
      const lists = await fetchWorkLists({ sort: 'created_at', order: 'desc' });
      setWorkLists(lists);
    } catch (error: any) {
      console.error('Failed to load work lists', error);
      message.warning(error?.message || 'Kunne ikke hente gemte arbejdslister');
    } finally {
      setWorkListsLoading(false);
    }
  }, []);

  const loadLabels = useCallback(async () => {
    setLabelsLoading(true);
    try {
      const res = await api('/api/labels');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente labels');
      }
      const data = (await res.json()) as Label[];
      setLabels(
        [...data].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      console.error('Failed to load labels', error);
      message.error(error?.message || 'Kunne ikke hente labels');
    } finally {
      setLabelsLoading(false);
    }
  }, []);

  const labelOptions = useMemo(
    () =>
      labels.map((label) => ({
        key: label.id,
        value: label
      })),
    [labels]
  );

  useEffect(() => {
    setCurrentPage((prev) => (prev === pageFromParams ? prev : pageFromParams));
  }, [pageFromParams]);

  useEffect(() => {
    let cancelled = false;

    async function loadBranches() {
      setBrancheLoading(true);
      try {
        const res = await api('/api/companies/branche-options');
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente brancher');
        }
        const data = (await res.json()) as { code: string | null; text: string }[];
        if (!cancelled) {
          setBrancheOptions(data.filter((item) => Boolean(item?.text?.trim())));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load branches', error);
          message.warning('Kunne ikke hente brancheliste');
        }
      } finally {
        if (!cancelled) {
          setBrancheLoading(false);
        }
      }
    }

    loadBranches();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadLabels();
  }, [loadLabels]);

  useEffect(() => {
    void loadWorkLists();
  }, [loadWorkLists]);

  const performSearch = useCallback(
    async (
      formValues: SearchFormValues,
      options?: {
        skipUpdateParams?: boolean;
        page?: number;
        saveAsList?: boolean;
        knownTotal?: number | null;
        targetWorkListId?: string | null;
      }
    ) => {
      const normalized = normalizeSearchValues(formValues);
      const nextPage = options?.page ?? 1;
      const saveAsList = options?.saveAsList ?? Boolean(formValues.saveAsList);
      setPageSize(normalized.limit);

      if (!hasSearchCriteria(normalized)) {
        message.warning('Angiv mindst ét søgekriterie');
        setResults([]);
        if (!options?.skipUpdateParams) {
          setSearchParams({}, { replace: true });
          lastExecutedQueryRef.current = '';
        }
        setCurrentPage(1);
        return;
      }

      if (normalized.name && normalized.name.length < 3) {
        message.warning('Navn skal mindst indeholde 3 tegn');
        return;
      }

      if (normalized.cvr) {
        const digits = normalized.cvr.replace(/\D+/g, '');
        if (digits.length !== 8) {
          message.warning('CVR-nummer skal bestå af præcis 8 cifre');
          return;
        }
      }

      if (normalized.status && normalized.status.length < 2) {
        message.warning('Status skal mindst indeholde 2 tegn');
        return;
      }

      if (normalized.municipality && normalized.municipality.length < 2) {
        message.warning('Kommune skal mindst indeholde 2 tegn');
        return;
      }

      if (
        normalized.minEmployees !== null &&
        normalized.maxEmployees !== null &&
        normalized.minEmployees > normalized.maxEmployees
      ) {
        message.warning('Mindste antal ansatte må ikke være større end største antal ansatte');
        return;
      }

      const params = buildQueryParams(normalized);
      const paramsWithPage = new URLSearchParams(params);
      applyPageParam(paramsWithPage, nextPage);
      lastExecutedQueryRef.current = paramsWithPage.toString();
      if (!options?.skipUpdateParams) {
        setSearchParams(paramsWithPage);
      }
      setCurrentPage(nextPage);

      const queryParams = new URLSearchParams(paramsWithPage);
      queryParams.set('limit', String(normalized.limit));
      const queryString = queryParams.toString();

      setLoading(true);
      try {
        const res = await api(`/api/companies/search?${queryString}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke søge efter virksomheder');
        }

        const payload = (await res.json()) as
          | {
              items?: CompanySearchResult[];
              total?: number | string | null;
              results?: CompanySearchResult[];
              total_results?: number | string | null;
            }
          | CompanySearchResult[];

        const list = Array.isArray(payload)
          ? payload
          : payload.items ?? payload.results ?? [];

        const headerTotal = Number(
          res.headers.get('x-total-count') ??
            res.headers.get('x-total') ??
            res.headers.get('x-total-results')
        );

        const totalFromPayload = Array.isArray(payload)
          ? payload.length
          : (() => {
              const candidate = payload.total ?? payload.total_results ?? null;
              if (typeof candidate === 'number') {
                return candidate;
              }
              if (typeof candidate === 'string') {
                const parsed = Number(candidate);
                return Number.isFinite(parsed) ? parsed : null;
              }
              return null;
            })();

        const normalizedResults = list
          .map((item) => ({
            ...item,
            cvrnummer: item.cvrnummer?.replace(/\D+/g, '') ?? '',
            leadId: item.leadId ?? null,
            hideInSearch: Boolean(item.hideInSearch),
            hasCustomer: Boolean(item.hasCustomer ?? (item as any).alreadyCustomer ?? (item as any).allerede_kunde),
            labels: normalizeAssignedLabels((item as any).labels),
            ansatte_aar: (() => {
              const raw = (item as any).ansatte_aar ?? (item as any).ansatteAar;
              if (raw === undefined || raw === null) {
                return null;
              }
              const parsed = Number(raw);
              return Number.isFinite(parsed) ? parsed : null;
            })(),
            ansatte_datotil:
              (item as any).ansatte_datotil ?? (item as any).ansatteDatotil ?? null,
            productionUnitCount: Number(
              item.productionUnitCount ?? (item as any).production_unit_count ?? 0
            )
          }))
          .filter((item) => item.cvrnummer);

        const pageItemCount = normalizedResults.length;
        const knownTotal = Number(options?.knownTotal);
        let derivedTotal =
          Number.isFinite(knownTotal) && knownTotal >= 0
            ? knownTotal
            : typeof totalFromPayload === 'number' && Number.isFinite(totalFromPayload) && totalFromPayload > 0
            ? totalFromPayload
            : Number.isFinite(headerTotal) && headerTotal > 0
              ? headerTotal
              : 0;

        if (!Number.isFinite(derivedTotal) || derivedTotal <= 0) {
          if (pageItemCount < normalized.limit) {
            derivedTotal = (nextPage - 1) * normalized.limit + pageItemCount;
          } else {
            derivedTotal = nextPage * normalized.limit + 1;
          }
        }

        setResults(normalizedResults);
        setTotalResults(derivedTotal);

        if (saveAsList) {
          try {
            const targetWorkListId = options?.targetWorkListId?.trim() || null;
            const updatesExistingWorkList = Boolean(targetWorkListId);
            const workListName = formValues.workListName?.trim();
            const generatedNameParts = [normalized.name, normalized.cvr ? `CVR ${normalized.cvr}` : '', normalized.municipality]
              .filter(Boolean)
              .join(' · ');
            const created = await createWorkListSaveJob({
              ...(updatesExistingWorkList
                ? { work_list_id: targetWorkListId }
                : { display_name: workListName || generatedNameParts || `Arbejdsliste ${new Date().toLocaleDateString('da-DK')}` }),
              search_criteria: {
                name: normalized.name,
                cvr: normalized.cvr,
                status: normalized.status,
                branche: normalized.branche,
                postal: normalized.postal,
                municipality: normalized.municipality,
                includeExistingLeads: normalized.includeExistingLeads,
                minEmployees: normalized.minEmployees,
                maxEmployees: normalized.maxEmployees,
                limit: normalized.limit
              }
            });
            const targetList = created.work_list;
            const navigationGeneration = ++workListNavigationGenerationRef.current;
            saveJobAutoOpenIntentRef.current = {
              jobId: created.job.id,
              generation: navigationGeneration
            };
            setSaveJob(created.job);
            message.success(
              updatesExistingWorkList
                ? `Gem-job startet for arbejdsliste: ${targetList.display_name}`
                : `Arbejdsliste oprettet og gem-job startet: ${targetList.display_name}`
            );
            await loadWorkLists();
            if (onWorkListCreated) {
              onWorkListCreated(targetList.id);
            }
          } catch (error: any) {
            console.error('Failed to save work list', error);
            message.error(error?.message || 'Kunne ikke starte gem-job');
          }
        }
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke søge efter virksomheder');
      } finally {
        setLoading(false);
      }
    },
    [setCurrentPage, setResults, setSearchParams, setPageSize, loadWorkLists, onWorkListCreated]
  );

  const cacheWorkListDetail = useCallback(
    (detail: WorkListDetailPayload, filters: WorkListToolbarFilters) => {
      const page = detail.page || 1;
      const limit = detail.limit || DEFAULT_PAGE_SIZE;
      const results = detail.items.map(mapWorkListItemToCompanyResult).filter((item) => item.cvrnummer);
      const total = detail.total || 0;
      const cached = { workList: detail.work_list, results, total, page, limit };
      if (!workListCacheGenerationRef.current.has(detail.work_list.id)) {
        workListCacheGenerationRef.current.set(detail.work_list.id, 0);
      }
      workListPageCacheRef.current.set(
        workListPageCacheKey(detail.work_list.id, page, limit, filters),
        cached
      );
      return cached;
    },
    []
  );

  const prefetchWorkListPage = useCallback(
    (
      workListId: string,
      page: number,
      limit: number,
      total: number,
      filters: WorkListToolbarFilters
    ) => {
      if (page < 1 || page > Math.ceil(Math.max(total, 1) / Math.max(limit, 1))) {
        return;
      }
      const key = workListPageCacheKey(workListId, page, limit, filters);
      if (workListPageCacheRef.current.has(key) || workListPrefetchingRef.current.has(key)) {
        return;
      }
      const generation = workListCacheGenerationRef.current.get(workListId) ?? 0;
      workListCacheGenerationRef.current.set(workListId, generation);
      workListPrefetchingRef.current.add(key);
      void fetchWorkListDetail(workListId, { ...filters, page, limit })
        .then((detail) => {
          if ((workListCacheGenerationRef.current.get(workListId) ?? 0) === generation) {
            cacheWorkListDetail(detail, filters);
          }
        })
        .catch(() => undefined)
        .finally(() => {
          workListPrefetchingRef.current.delete(key);
        });
    },
    [cacheWorkListDetail]
  );

  const openWorkList = useCallback(
    async (workListId: string, preserveSaveJobIntent = false) => {
      if (!preserveSaveJobIntent) {
        workListNavigationGenerationRef.current += 1;
      }
      const requestId = ++workListViewRequestRef.current;
      setLoading(true);
      try {
        const filters = { ...DEFAULT_WORK_LIST_FILTERS };
        const detail = await fetchWorkListDetail(workListId, {
          ...filters,
          page: 1,
          limit: DEFAULT_PAGE_SIZE
        });
        if (requestId !== workListViewRequestRef.current) {
          return;
        }
        const values = {
          ...getValuesFromCriteria(detail.work_list.search_criteria),
          limit: DEFAULT_PAGE_SIZE,
          saveAsList: false,
          workListName: detail.work_list.display_name
        };
        currentWorkListViewRef.current = {
          workListId,
          filters: { ...filters },
          page: 1,
          limit: DEFAULT_PAGE_SIZE
        };
        activeWorkListIdRef.current = detail.work_list.id;
        setActiveWorkList(detail.work_list);
        form.setFieldsValue({
          ...values,
          includeExistingLeads: values.includeExistingLeads ?? false,
          limit: values.limit ?? DEFAULT_PAGE_SIZE,
          minEmployees: values.minEmployees ?? undefined,
          maxEmployees: values.maxEmployees ?? undefined
        });
        setWorkListFilterDraft(filters);
        setAppliedWorkListFilters(filters);
        setWorkListDraftValues({});
        setPageSize(DEFAULT_PAGE_SIZE);
        setCurrentPage(1);
        const cached = cacheWorkListDetail(detail, filters);
        setResults(cached.results);
        setTotalResults(cached.total);
        prefetchWorkListPage(workListId, 2, DEFAULT_PAGE_SIZE, cached.total, filters);
      } catch (error: any) {
        if (requestId === workListViewRequestRef.current) {
          message.error(error?.message || 'Kunne ikke åbne arbejdsliste');
        }
      } finally {
        if (requestId === workListViewRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [cacheWorkListDetail, form, prefetchWorkListPage]
  );

  const loadWorkListView = useCallback(
    async (
      workListId: string,
      filters: WorkListToolbarFilters,
      page: number,
      limit: number,
      force = false
    ) => {
      workListNavigationGenerationRef.current += 1;
      const requestId = ++workListViewRequestRef.current;
      const cacheKey = workListPageCacheKey(workListId, page, limit, filters);
      const cached = force ? null : workListPageCacheRef.current.get(cacheKey);
      if (cached) {
        if (requestId !== workListViewRequestRef.current) {
          return;
        }
        setLoading(false);
        currentWorkListViewRef.current = {
          workListId,
          filters: { ...filters },
          page,
          limit
        };
        activeWorkListIdRef.current = cached.workList.id;
        setActiveWorkList(cached.workList);
        setResults(cached.results);
        setTotalResults(cached.total);
        setCurrentPage(page);
        setPageSize(limit);
        prefetchWorkListPage(workListId, page + 1, limit, cached.total, filters);
        prefetchWorkListPage(workListId, page - 1, limit, cached.total, filters);
        return;
      }

      setLoading(true);
      try {
        let resolvedPage = page;
        let detail = await fetchWorkListDetail(workListId, { ...filters, page: resolvedPage, limit });
        if (requestId !== workListViewRequestRef.current) {
          return;
        }
        const maxPage = Math.max(1, Math.ceil(Math.max(detail.total, 1) / Math.max(limit, 1)));
        if (resolvedPage > maxPage) {
          resolvedPage = maxPage;
          detail = await fetchWorkListDetail(workListId, {
            ...filters,
            page: resolvedPage,
            limit
          });
          if (requestId !== workListViewRequestRef.current) {
            return;
          }
        }
        const nextCached = cacheWorkListDetail(detail, filters);
        currentWorkListViewRef.current = {
          workListId,
          filters: { ...filters },
          page: resolvedPage,
          limit
        };
        activeWorkListIdRef.current = nextCached.workList.id;
        setActiveWorkList(nextCached.workList);
        setResults(nextCached.results);
        setTotalResults(nextCached.total);
        setCurrentPage(resolvedPage);
        setPageSize(limit);
        prefetchWorkListPage(workListId, resolvedPage + 1, limit, nextCached.total, filters);
        prefetchWorkListPage(workListId, resolvedPage - 1, limit, nextCached.total, filters);
      } finally {
        if (requestId === workListViewRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [cacheWorkListDetail, prefetchWorkListPage]
  );

  const clearWorkListPageCache = useCallback((workListId: string) => {
    workListCacheGenerationRef.current.set(
      workListId,
      (workListCacheGenerationRef.current.get(workListId) ?? 0) + 1
    );
    for (const key of Array.from(workListPageCacheRef.current.keys())) {
      if (key.startsWith(`${workListId}:`)) {
        workListPageCacheRef.current.delete(key);
      }
    }
  }, []);

  const clearAllWorkListPageCaches = useCallback(() => {
    for (const [workListId, generation] of Array.from(workListCacheGenerationRef.current.entries())) {
      workListCacheGenerationRef.current.set(workListId, generation + 1);
    }
    workListPageCacheRef.current.clear();
  }, []);

  const handleWorkListToolbarSearch = useCallback(async () => {
    if (!activeWorkList) {
      return;
    }
    const filters = {
      ...workListFilterDraft,
      q: workListFilterDraft.q.trim()
    };
    setAppliedWorkListFilters(filters);
    try {
      await loadWorkListView(activeWorkList.id, filters, 1, pageSize);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke filtrere arbejdslisten');
    }
  }, [activeWorkList, loadWorkListView, pageSize, workListFilterDraft]);

  const handleCloseWorkList = useCallback(() => {
    workListNavigationGenerationRef.current += 1;
    workListRefreshGenerationRef.current += 1;
    workListViewRequestRef.current += 1;
    activeWorkListIdRef.current = null;
    currentWorkListViewRef.current = null;
    saveJobAutoOpenIntentRef.current = null;
    setSaveJob(null);
    setLoading(false);
    setWorkListRefreshing(false);
    setActiveWorkList(null);
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setWorkListFilterDraft(DEFAULT_WORK_LIST_FILTERS);
    setAppliedWorkListFilters(DEFAULT_WORK_LIST_FILTERS);
    setWorkListDraftValues({});
  }, []);

  const handleRefreshWorkList = useCallback(async () => {
    if (!activeWorkList) {
      return;
    }
    const workListId = activeWorkList.id;
    const refreshGeneration = ++workListRefreshGenerationRef.current;
    setWorkListRefreshing(true);
    try {
      const summary = await refreshWorkList(workListId);
      clearWorkListPageCache(workListId);
      if (refreshGeneration !== workListRefreshGenerationRef.current) {
        return;
      }
      const currentView = currentWorkListViewRef.current;
      if (
        activeWorkListIdRef.current === workListId &&
        currentView?.workListId === workListId
      ) {
        await loadWorkListView(
          workListId,
          currentView.filters,
          currentView.page,
          currentView.limit,
          true
        );
        if (refreshGeneration !== workListRefreshGenerationRef.current) {
          return;
        }
      }
      await loadWorkLists();
      if (refreshGeneration !== workListRefreshGenerationRef.current) {
        return;
      }
      message.success(
        `Arbejdsliste opdateret: ${summary.inserted} nye, ${summary.updated} opdaterede`
      );
    } catch (error: any) {
      if (refreshGeneration === workListRefreshGenerationRef.current) {
        message.error(error?.message || 'Kunne ikke opdatere arbejdslisten');
      }
    } finally {
      if (refreshGeneration === workListRefreshGenerationRef.current) {
        setWorkListRefreshing(false);
      }
    }
  }, [
    activeWorkList,
    clearWorkListPageCache,
    loadWorkListView,
    loadWorkLists
  ]);

  const handleExportWorkList = useCallback(async () => {
    if (!activeWorkList) {
      return;
    }
    setWorkListExporting(true);
    try {
      const blob = await exportWorkListExcel(activeWorkList.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = activeWorkList.display_name.replace(/[<>:"/\\|?*]+/g, '-').trim();
      link.href = objectUrl;
      link.download = `${safeName || 'arbejdsliste'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke eksportere arbejdslisten');
    } finally {
      setWorkListExporting(false);
    }
  }, [activeWorkList]);

  useEffect(() => {
    if (!initialWorkListId) {
      return;
    }
    void openWorkList(initialWorkListId);
  }, [initialWorkListId, openWorkList]);

  useEffect(() => {
    if (!saveJob || !['PENDING', 'RUNNING'].includes(saveJob.status)) {
      return;
    }

    let cancelled = false;
    const interval = setInterval(() => {
      void (async () => {
        try {
          const latest = await fetchWorkListSaveJob(saveJob.id);
          if (cancelled) {
            return;
          }

          const justCompleted = latest.status === 'COMPLETED' && saveJob.status !== 'COMPLETED';
          const justFailed = latest.status === 'FAILED' && saveJob.status !== 'FAILED';

          setSaveJob(latest);

          if (justCompleted) {
            message.success(`Gem-job færdigt: ${latest.insertedCount} nye, ${latest.updatedCount} opdaterede`);
            await loadWorkLists();
            const autoOpenIntent = saveJobAutoOpenIntentRef.current;
            if (
              cancelled ||
              autoOpenIntent?.jobId !== latest.id ||
              autoOpenIntent.generation !== workListNavigationGenerationRef.current
            ) {
              return;
            }
            await openWorkList(latest.workListId, true);
          } else if (justFailed) {
            message.error(latest.errorMessage || 'Gem-job fejlede');
          }
        } catch (error: any) {
          if (!cancelled) {
            message.warning(error?.message || 'Kunne ikke opdatere jobstatus');
          }
        }
      })();
    }, 1500);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [saveJob, loadWorkLists, openWorkList]);

  const handleAssignLabel = useCallback(
    async (record: CompanySearchResult, labelId: string) => {
      setAssigningLabelFor(record.cvrnummer);
      try {
        const cvr = onlyDigits(record.cvrnummer);
        if (cvr.length !== 8) {
          throw new Error('Ugyldigt CVR-nummer');
        }

        const hasLabel = Array.isArray(record.labels) && record.labels.some((label) => label.id === labelId);
        const method: 'POST' | 'DELETE' = hasLabel ? 'DELETE' : 'POST';
        const path = hasLabel
          ? `/api/companies/${cvr}/labels/${labelId}`
          : `/api/companies/${cvr}/labels`;

        const res = await api(path, {
          method,
          headers: method === 'POST' ? { 'Content-Type': 'application/json' } : undefined,
          body: method === 'POST' ? JSON.stringify({ labelId }) : undefined
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke opdatere labels');
        }

        const payload = (await res.json().catch(() => ({}))) as {
          labels?: Label[];
          hideInSearch?: boolean;
          label?: Label | null;
        };
        const nextLabels = normalizeAssignedLabels(payload.labels);
        const nextHide = Boolean(payload.hideInSearch);

        clearAllWorkListPageCaches();

        if (nextHide && !activeWorkList) {
          setResults((prev) => prev.filter((item) => onlyDigits(item.cvrnummer) !== cvr));
        } else {
          setResults((prev) =>
            prev.map((item) =>
              onlyDigits(item.cvrnummer) === cvr
                ? {
                    ...item,
                    labels: nextLabels,
                    hideInSearch: nextHide
                  }
                : item
            )
          );
        }

        if (activeWorkList) {
          const currentView = currentWorkListViewRef.current;
          if (currentView?.workListId === activeWorkList.id) {
            try {
              await loadWorkListView(
                currentView.workListId,
                currentView.filters,
                currentView.page,
                currentView.limit,
                true
              );
            } catch (reloadError: any) {
              message.warning(reloadError?.message || 'Label blev gemt, men listen kunne ikke genindlæses');
            }
          }
        }

        message.success(hasLabel ? 'Label fjernet fra CVR' : 'Label tilknyttet CVR');
      } catch (error: any) {
        console.error('Failed to update labels from search', error);
        message.error(error?.message || 'Kunne ikke opdatere labels');
      } finally {
        setAssigningLabelFor(null);
      }
    },
    [activeWorkList, clearAllWorkListPageCaches, loadWorkListView]
  );

  useEffect(() => {
    form.setFieldsValue({
      ...paramsValues,
      includeExistingLeads: paramsValues.includeExistingLeads ?? false,
      limit: paramsValues.limit ?? DEFAULT_PAGE_SIZE,
      minEmployees: paramsValues.minEmployees ?? undefined,
      maxEmployees: paramsValues.maxEmployees ?? undefined
    });

    const currentQuery = searchParamsKey;

    if (!currentQuery) {
      setResults([]);
      lastExecutedQueryRef.current = '';
      return;
    }

    if (currentQuery === lastExecutedQueryRef.current) {
      return;
    }

    lastExecutedQueryRef.current = currentQuery;
    void performSearch(paramsValues, { skipUpdateParams: true, page: pageFromParams });
  }, [form, paramsValues, performSearch, searchParamsKey, setResults, pageFromParams]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setResults([]);
    setTotalResults(0);
    workListNavigationGenerationRef.current += 1;
    activeWorkListIdRef.current = null;
    currentWorkListViewRef.current = null;
    saveJobAutoOpenIntentRef.current = null;
    setActiveWorkList(null);
    setSaveJob(null);
    setSearchParams({}, { replace: true });
    lastExecutedQueryRef.current = '';
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  }, [form, setResults, setSearchParams, setPageSize]);

  const openDetail = useCallback(
    (record: CompanySearchResult) => {
      const cvr = onlyDigits(record.cvrnummer);
      if (!cvr || cvr.length !== 8) {
        message.error('CVR-nummer mangler eller er ugyldigt');
        return;
      }

      setDetailVisible(true);
      setDetailLoading(true);
      setPropertyDetailLoading(true);
      setPropertyChangesLoading(true);
      setInsuranceEventsLoading(true);
      setDetail(buildSummaryPreview(record));
      setDetailCvr(cvr);
      setPropertyDetail(null);
      setPropertyDetailError(null);
      setPropertyChanges(null);
      setPropertyChangesError(null);
      setInsuranceEvents(null);
      setInsuranceEventsError(null);

      void (async () => {
        try {
        const res = await api(`/api/companies/${cvr}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente detaljer');
        }
        const data = (await res.json()) as CvrSummary;
        setDetail(data);
        } catch (error: any) {
          message.error(error?.message || 'Kunne ikke hente detaljer');
        } finally {
          setDetailLoading(false);
        }
      })();

      void (async () => {
        try {
          const res = await api(`/api/companies/${cvr}/property-register`);
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((payload as any)?.error || 'Kunne ikke hente DAR/BBR-data');
          }
          setPropertyDetail(payload as CompanyPropertyRegisterLookup);
          try {
            const changesRes = await api(`/api/companies/${cvr}/property-register/changes?limit=10`);
            const changesPayload = await changesRes.json().catch(() => ({}));
            if (!changesRes.ok) {
              throw new Error((changesPayload as any)?.error || 'Kunne ikke hente registerændringer');
            }
            setPropertyChanges(changesPayload as CompanyPropertyRegisterChanges);
          } catch (changesError: any) {
            setPropertyChangesError(changesError?.message || 'Kunne ikke hente registerændringer');
            setPropertyChanges(null);
          }
          try {
            const insuranceRes = await api(`/api/companies/${cvr}/property-register/insurance-events?limit=10`);
            const insurancePayload = await insuranceRes.json().catch(() => ({}));
            if (!insuranceRes.ok) {
              throw new Error((insurancePayload as any)?.error || 'Kunne ikke hente forsikringssignaler');
            }
            setInsuranceEvents(insurancePayload as CompanyInsuranceRelevanceEvents);
          } catch (insuranceError: any) {
            setInsuranceEventsError(insuranceError?.message || 'Kunne ikke hente forsikringssignaler');
            setInsuranceEvents(null);
          }
        } catch (error: any) {
          setPropertyDetailError(error?.message || 'Kunne ikke hente DAR/BBR-data');
          setPropertyDetail(null);
        } finally {
          setPropertyDetailLoading(false);
          setPropertyChangesLoading(false);
          setInsuranceEventsLoading(false);
        }
      })();
    },
    []
  );

  const openAiModal = useCallback(
    async (cvr: string) => {
      const digits = onlyDigits(cvr);
      if (!digits || digits.length !== 8) {
        message.error('CVR-nummer mangler eller er ugyldigt');
        return;
      }

      setAiModalVisible(true);
      setAiModalLoading(true);
      setAiSummary(null);
      setAiSummaryCvr(digits);
      setAiAnalyzingFor(digits);

      try {
        const res = await api(`/api/companies/${digits}/ai-summary`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente AI-analyse');
        }
        const data = (await res.json()) as CompanyAiSummary;
        setAiSummary(data);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke hente AI-analyse');
        setAiSummary(null);
      } finally {
        setAiModalLoading(false);
        setAiAnalyzingFor(null);
      }
    },
    []
  );

  const openContactModal = useCallback(
    async (cvr: string) => {
      const digits = onlyDigits(cvr);
      if (!digits || digits.length !== 8) {
        message.error('CVR-nummer mangler eller er ugyldigt');
        return;
      }

      setContactModalVisible(true);
      setContactModalLoading(true);
      setContactLookup(null);
      setContactLookupCvr(digits);
      setContactLoadingFor(digits);

      try {
        const res = await api(`/api/companies/${digits}/contact-candidates`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente kontaktoplysninger');
        }
        const data = (await res.json()) as CompanyContactLookup;
        setContactLookup(data);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke hente kontaktoplysninger');
        setContactLookup(null);
      } finally {
        setContactModalLoading(false);
        setContactLoadingFor(null);
      }
    },
    []
  );

  const handleQuickCall = useCallback(
    async (record: CompanySearchResult) => {
        const rawCvr = record.cvrnummer ? record.cvrnummer.replace(/\D+/g, '') : '';
        if (!rawCvr || rawCvr.length !== 8) {
          message.error('CVR-nummer mangler eller er ugyldigt');
          return;
        }

        setQuickCallTarget(rawCvr);
        try {
          const payload = {
            company_name: record.navn || undefined,
            cvrnummer: rawCvr,
            status: record.virksomhed_status || undefined,
            branchetekst: record.branchetekst || undefined,
            kommunenavn: record.kommunenavn || undefined,
            postnummer: record.postnummer || undefined,
            postdistrikt: record.postdistrikt || undefined,
            telefon: record.telefon || undefined,
            email: record.email || undefined
          };

          const res = await api('/api/leads/quick-call', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Kunne ikke registrere opkald');
          }

          const data = (await res.json()) as {
            leadId?: string | null;
            callAttemptCount?: number;
            hidden?: boolean;
            created?: boolean;
            lastCallAt?: string | null;
          };

          setResults((prev) =>
            prev.map((item) => {
              if (item.cvrnummer !== record.cvrnummer) {
                return item;
              }

              const callAttemptCount =
                data.callAttemptCount === undefined || data.callAttemptCount === null
                  ? item.callAttemptCount
                  : data.callAttemptCount;

              const lastCallAt = data.lastCallAt ?? item.lastCallAt;
              const hidden = Boolean(data.hidden);

              return {
                ...item,
                leadId: data.leadId ?? item.leadId,
                hasVisibleLead: hidden ? item.hasVisibleLead : true,
                hasHiddenLead: hidden ? true : item.hasHiddenLead,
                callAttemptCount,
                lastCallAt
              };
            })
          );

          const lastCallShort = formatDateTimeShort(data.lastCallAt ?? null);
          const successParts: string[] = [];
          if (data.created) {
            successParts.push(data.hidden ? 'Lead oprettet (skjult)' : 'Lead oprettet');
          }
          successParts.push('Opkald registreret');
          if (lastCallShort) {
            successParts.push(`senest ${lastCallShort}`);
          }
          message.success(successParts.join(' · '));

          if (data.created && onLeadCreated) {
            await onLeadCreated();
          }
        } catch (error: any) {
          message.error(error?.message || 'Kunne ikke registrere opkald');
        } finally {
          setQuickCallTarget(null);
        }
    },
    [onLeadCreated, setResults]
  );

  const handleTableChange = useCallback(
    (paginationConfig: TablePaginationConfig) => {
      const nextPage = paginationConfig.current ?? 1;
      const nextPageSize = paginationConfig.pageSize ?? DEFAULT_PAGE_SIZE;
      if (activeWorkList) {
        void loadWorkListView(
          activeWorkList.id,
          appliedWorkListFilters,
          nextPage,
          nextPageSize
        )
          .catch((error: any) => {
            message.error(error?.message || 'Kunne ikke hente arbejdsliste-side');
          });
        return;
      }
      const currentValues = normalizeSearchValues(form.getFieldsValue());
      setPageSize(nextPageSize);
      void performSearch(
        { ...currentValues, limit: nextPageSize, saveAsList: false },
        { page: nextPage, saveAsList: false, knownTotal: activeWorkList?.item_count }
      );
    },
    [activeWorkList, appliedWorkListFilters, form, loadWorkListView, performSearch]
  );

  useEffect(() => {
    if (activeWorkList) {
      return;
    }
    if (totalResults <= 0) {
      return;
    }
    const maxPage = Math.max(1, Math.ceil(Math.max(totalResults, 1) / Math.max(pageSize, 1)));
    if (currentPage > maxPage) {
      const currentValues = normalizeSearchValues(form.getFieldsValue());
      void performSearch(currentValues, { page: maxPage });
    }
  }, [activeWorkList, totalResults, pageSize, currentPage, form, performSearch]);

  const handleCreateLead = useCallback(
    async (cvr: string) => {
      setLeadCreatingFor(cvr);
      try {
        const res = await api(`/api/companies/${cvr}/lead`, { method: 'POST' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke oprette lead');
        }
        message.success('Virksomheden er oprettet som lead');
        setResults((prev) => prev.filter((item) => item.cvrnummer !== cvr));
        if (onLeadCreated) {
          await onLeadCreated();
        }
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette lead');
      } finally {
        setLeadCreatingFor(null);
      }
    },
    [onLeadCreated, setResults]
  );

  const handleOpenLead = useCallback((leadId: string) => {
    setLeadDetailId(leadId);
    setLeadDetailVisible(true);
  }, []);

  const closeLeadDetail = useCallback(() => {
    setLeadDetailVisible(false);
    setLeadDetailId(null);
  }, []);

  const handleLeadDetailBack = useCallback(
    (_state?: { fromCompanySearch?: string | null }) => {
      closeLeadDetail();
    },
    [closeLeadDetail]
  );

  const handleLeadUpdated = useCallback(() => {
    if (!lastExecutedQueryRef.current) {
      return;
    }
    const rawValues = form.getFieldsValue() as SearchFormValues;
    const normalized = normalizeSearchValues(rawValues);
    if (!hasSearchCriteria(normalized)) {
      return;
    }
    void performSearch({ ...rawValues, limit: normalized.limit, saveAsList: false }, {
      skipUpdateParams: true,
      page: currentPage,
      saveAsList: false
    });
  }, [currentPage, form, performSearch]);

  const handleWorkListItemUpdate = useCallback(
    async (itemId: string, updates: Partial<Pick<WorkListItem, 'email_sendt' | 'bemaerkninger' | 'allerede_kunde' | 'ignorer'>>) => {
      if (!activeWorkList) return;
      const workListId = activeWorkList.id;
      
      setWorkListUpdatingFor(itemId);
      try {
        const updated = await updateWorkListItem(workListId, itemId, updates);
        clearWorkListPageCache(workListId);
        if (activeWorkListIdRef.current !== workListId) {
          return;
        }
        setResults((prev) =>
          prev.map((result) => {
            if (result.cvrnummer !== updated.company_cvr) {
              return result;
            }
            const mapped = mapWorkListItemToCompanyResult(updated);
            return {
              ...mapped,
              labels: result.labels,
              hideInSearch: result.hideInSearch
            };
          })
        );
        const currentView = currentWorkListViewRef.current;
        if (!currentView || currentView.workListId !== workListId) {
          return;
        }
        const affectsActiveFilter =
          (updates.email_sendt !== undefined && currentView.filters.emailSent !== 'ALL') ||
          (updates.allerede_kunde !== undefined &&
            currentView.filters.alreadyCustomer !== 'ALL') ||
          (updates.ignorer !== undefined && currentView.filters.ignored !== 'ALL');
        if (affectsActiveFilter) {
          try {
            await loadWorkListView(
              workListId,
              currentView.filters,
              1,
              currentView.limit,
              true
            );
          } catch (reloadError: any) {
            message.warning(
              reloadError?.message || 'Ændringen blev gemt, men listen kunne ikke genindlæses'
            );
          }
        }
        message.success('Opdateret');
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere');
      } finally {
        setWorkListUpdatingFor((current) => (current === itemId ? null : current));
      }
    },
    [
      activeWorkList,
      clearWorkListPageCache,
      loadWorkListView
    ]
  );

  const handleWorkListItemEnrichment = useCallback(
    async (itemId: string, updates: Partial<Pick<WorkListItem,
      'beriget_hjemmeside' | 'beriget_kontakt' | 'beriget_telefon' | 'beriget_email' | 'bemaerkninger'
    >>) => {
      if (!activeWorkList) return;
      const workListId = activeWorkList.id;
      const viewRequestId = workListViewRequestRef.current;
      const queueKey = `${workListId}:${itemId}`;
      const generation = (workListEnrichmentGenerationRef.current.get(queueKey) ?? 0) + 1;
      workListEnrichmentGenerationRef.current.set(queueKey, generation);
      const previous = workListEnrichmentQueueRef.current.get(queueKey) ?? Promise.resolve();
      const queuedRequest = previous
        .catch(() => undefined)
        .then(() => updateWorkListItemEnrichment(workListId, itemId, updates));
      const completion = queuedRequest.then(
        () => undefined,
        () => undefined
      );
      workListEnrichmentQueueRef.current.set(queueKey, completion);

      setWorkListUpdatingFor(itemId);
      try {
        const updated = await queuedRequest;
        clearWorkListPageCache(workListId);
        if (
          activeWorkListIdRef.current !== workListId ||
          workListEnrichmentGenerationRef.current.get(queueKey) !== generation
        ) {
          return;
        }
        if (viewRequestId === workListViewRequestRef.current) {
          setResults((prev) =>
            prev.map((r) => {
              if (r.cvrnummer !== updated.company_cvr) {
                return r;
              }
              const mapped = mapWorkListItemToCompanyResult(updated);
              return {
                ...mapped,
                labels: r.labels,
                hideInSearch: r.hideInSearch
              };
            })
          );
        } else {
          const currentView = currentWorkListViewRef.current;
          if (currentView?.workListId === workListId) {
            try {
              await loadWorkListView(
                workListId,
                currentView.filters,
                currentView.page,
                currentView.limit,
                true
              );
            } catch (reloadError: any) {
              message.warning(
                reloadError?.message || 'Berigelsen blev gemt, men listen kunne ikke genindlæses'
              );
            }
          }
        }
        setWorkListDraftValues((prev) => {
          const current = prev[itemId];
          if (!current) {
            return prev;
          }
          const next = { ...current };
          const enrichmentKeys = [
            'beriget_hjemmeside',
            'beriget_kontakt',
            'beriget_telefon',
            'beriget_email',
            'bemaerkninger'
          ] as const;
          for (const key of enrichmentKeys) {
            const draftValue = current[key];
            if (draftValue === undefined) {
              continue;
            }
            const normalizedDraft = draftValue.trim() || null;
            if (normalizedDraft === (updated[key] ?? null)) {
              delete next[key];
            }
          }
          const draftKeys = Object.keys(next);
          if (draftKeys.length === 0) {
            const copy = { ...prev };
            delete copy[itemId];
            return copy;
          }
          return { ...prev, [itemId]: next };
        });
        message.success('Berigelse opdateret');
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere berigelse');
      } finally {
        if (workListEnrichmentQueueRef.current.get(queueKey) === completion) {
          workListEnrichmentQueueRef.current.delete(queueKey);
        }
        if (workListEnrichmentGenerationRef.current.get(queueKey) === generation) {
          workListEnrichmentGenerationRef.current.delete(queueKey);
          setWorkListUpdatingFor((current) => (current === itemId ? null : current));
        }
      }
    },
    [activeWorkList, clearWorkListPageCache, loadWorkListView]
  );

  const columns: ColumnsType<CompanySearchResult> = useMemo(
    () => [
      {
        title: 'Virksomhed',
        key: 'company',
        width: 360,
        render: (_value, record) => (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text strong>{record.navn || '(navn mangler)'}</Text>
            <Space size={6} wrap>
              <Text type="secondary">CVR {formatCvrNumber(record.cvrnummer)}</Text>
              <Tag color={statusColor(record.virksomhed_status)}>{record.virksomhed_status || 'Ukendt'}</Tag>
              {record.hasCustomer ? <Tag color="cyan">Kunde</Tag> : null}
              {record.leadId ? <Tag color={record.hasVisibleLead ? 'green' : 'orange'}>Lead</Tag> : null}
            </Space>
            <Text type="secondary">
              {[record.postnummer, record.postdistrikt, record.kommunenavn].filter(Boolean).join(' · ') || 'Adresse mangler'}
            </Text>
          </Space>
        )
      },
      {
        title: 'Profil',
        key: 'profile',
        width: 320,
        render: (_value, record) => (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text>{record.branchetekst || 'Branche mangler'}</Text>
            <Space size={6} wrap>
              <Tag>Ansatte: {formatEmployeeCountLabel((record as any).ansatte_aar)}</Tag>
              <Tag>P-numre: {typeof record.productionUnitCount === 'number' && Number.isFinite(record.productionUnitCount) ? record.productionUnitCount : '—'}</Tag>
            </Space>
          </Space>
        )
      },
      {
        title: 'Kontakt og aktivitet',
        key: 'activity',
        width: 260,
        render: (_value, record) => {
          const shortLabel = formatDateTimeShort(record.lastCallAt) || null;
          const fullLabel = formatDateTimeFull(record.lastCallAt) || undefined;
          return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text>{record.email || 'E-mail mangler'}</Text>
              <Text type="secondary">Opdateret {formatUpdated(record.opdateret_tidspunkt)}</Text>
              {record.callAttemptCount ? (
                <Tooltip
                  title={fullLabel || 'Seneste opkaldstidspunkt er ikke tilgængeligt'}
                  placement="topLeft"
                >
                  <Tag color="processing">
                    Forsøg: {record.callAttemptCount}
                    {shortLabel ? ` · ${shortLabel}` : ''}
                  </Tag>
                </Tooltip>
              ) : (
                <Tag color="default">Ingen opkald</Tag>
              )}
            </Space>
          );
        }
      },
      {
        title: 'Label',
        key: 'assignLabel',
        render: (_value, record) => {
          const assignedLabels = Array.isArray(record.labels) ? record.labels : [];
          const assignedLabelIds = new Set((record.labels || []).map((label) => label.id));
          const items = labelOptions.map((entry) => {
            const label = entry.value;
            const selected = assignedLabelIds.has(label.id);
            return {
              key: label.id,
              label: (
                <Space size={4} align="center">
                  {selected ? <Text strong>✓</Text> : <Text type="secondary">•</Text>}
                  <Tag
                    color={label.color || DEFAULT_LABEL_COLOR}
                    style={{ minWidth: 72, textAlign: 'center', fontWeight: 600 }}
                  >
                    {label.name}
                  </Tag>
                  {label.hideInSearch ? <Text type="secondary">Skjul i søgeresultat</Text> : null}
                </Space>
              )
            };
          });
          const menu: MenuProps = {
            items,
            onClick: ({ key }) => handleAssignLabel(record, String(key))
          };
          const disabled = labelsLoading || items.length === 0;
          return (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {assignedLabels.length > 0 ? (
                <Space size={4} wrap>
                  {assignedLabels.map((label) => (
                    <Tag key={label.id} color={label.color || DEFAULT_LABEL_COLOR}>
                      {label.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">Ingen labels</Text>
              )}
              <Dropdown menu={menu} trigger={['click']}>
                <Button
                  size="small"
                  disabled={disabled}
                  loading={assigningLabelFor === record.cvrnummer}
                >
                  Tildel label
                </Button>
              </Dropdown>
            </Space>
          );
        }
      },
      {
        title: 'Handlinger',
        key: 'actions',
        render: (_value, record) => (
          <Space>
            <Tooltip title="Vis detaljer">
              <Button
                aria-label="Vis detaljer"
                icon={<EyeOutlined />}
                onClick={() => openDetail(record)}
                shape="circle"
                size="small"
                type="text"
              />
            </Tooltip>
            <Tooltip title="AI-analyse">
              <Button
                aria-label="AI-analyse"
                icon={<RobotOutlined />}
                onClick={() => openAiModal(record.cvrnummer)}
                shape="circle"
                size="small"
                type="default"
                loading={aiAnalyzingFor === onlyDigits(record.cvrnummer)}
              />
            </Tooltip>
            <Tooltip title="Kontaktoplysninger">
              <Button
                aria-label="Kontaktoplysninger"
                icon={<ContactsOutlined />}
                onClick={() => openContactModal(record.cvrnummer)}
                shape="circle"
                size="small"
                type="default"
                loading={contactLoadingFor === onlyDigits(record.cvrnummer)}
              />
            </Tooltip>
            {record.leadId ? (
              <Tooltip title="Gå til lead">
                <Button
                  aria-label="Gå til lead"
                  onClick={() => handleOpenLead(record.leadId!)}
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Vis lead
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Opret lead">
                <Button
                  aria-label="Opret lead"
                  icon={<PlusOutlined />}
                  onClick={() => handleCreateLead(record.cvrnummer)}
                  loading={leadCreatingFor === record.cvrnummer}
                  shape="circle"
                  size="small"
                  type="primary"
                />
              </Tooltip>
            )}
            <Tooltip title="Registrer opkald">
              <Button
                aria-label="Registrer opkald"
                icon={<PhoneOutlined />}
                onClick={() => handleQuickCall(record)}
                loading={quickCallTarget === record.cvrnummer}
                shape="circle"
                size="small"
                type="default"
              />
            </Tooltip>
          </Space>
        )
      }
    ],
    [
      assigningLabelFor,
      handleAssignLabel,
      handleCreateLead,
      handleOpenLead,
      openAiModal,
      openContactModal,
      handleQuickCall,
      labelOptions,
      labelsLoading,
      leadCreatingFor,
      openDetail,
      quickCallTarget,
      aiAnalyzingFor,
      contactLoadingFor
    ]
  );

  const combinedWorkListColumns: ColumnsType<CompanySearchResult> = useMemo(
    () => {
      const companyRender = columns[0]?.render;
      const labelRender = columns[3]?.render;
      const actionsRender = columns[4]?.render;

      return [
        {
          ...columns[0],
          width: 360,
          render: (value: unknown, record: CompanySearchResult, index: number) => {
            const rowData = (record as any).row_data || record;
            return (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {companyRender ? companyRender(value, record, index) : null}
                <Space size={4} wrap>
                  <Tag>Raw data</Tag>
                  {normalizeNullableText((rowData as any).email ?? record.email) ? <Tag>E-mail: {normalizeNullableText((rowData as any).email ?? record.email)}</Tag> : null}
                  {normalizeNullableText((rowData as any).telefon ?? record.telefon) ? <Tag>Telefon: {normalizeNullableText((rowData as any).telefon ?? record.telefon)}</Tag> : null}
                  {normalizeNullableText((rowData as any).branchetekst ?? (rowData as any).branche ?? record.branchetekst) ? (
                    <Tag>Branche: {normalizeNullableText((rowData as any).branchetekst ?? (rowData as any).branche ?? record.branchetekst)}</Tag>
                  ) : null}
                  {([rowData.postnummer, rowData.postdistrikt].filter(Boolean).join(' ') || '') ? (
                    <Tag>Adresse: {[rowData.postnummer, rowData.postdistrikt].filter(Boolean).join(' ')}</Tag>
                  ) : null}
                  <Tag>Ansatte: {formatEmployeeCountLabel((record as any).ansatte_aar)}</Tag>
                  <Tag>P-numre: {typeof record.productionUnitCount === 'number' && Number.isFinite(record.productionUnitCount) ? record.productionUnitCount : '—'}</Tag>
                </Space>
              </Space>
            );
          }
        },
        {
          title: 'Status',
          key: 'workListStatus',
          width: 180,
          render: (_value: unknown, record: CompanySearchResult) => {
            const workListRecord = record as any;
            const disabled = workListUpdatingFor === workListRecord.id;
            return (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Checkbox checked={workListRecord.email_sendt || false} onChange={(e) => void handleWorkListItemUpdate(workListRecord.id, { email_sendt: e.target.checked })} disabled={disabled}>
                  Email sendt
                </Checkbox>
                <Checkbox checked={workListRecord.allerede_kunde || false} onChange={(e) => void handleWorkListItemUpdate(workListRecord.id, { allerede_kunde: e.target.checked })} disabled={disabled}>
                  Allerede kunde
                </Checkbox>
                <Checkbox checked={workListRecord.ignorer || false} onChange={(e) => void handleWorkListItemUpdate(workListRecord.id, { ignorer: e.target.checked })} disabled={disabled}>
                  Ignorer
                </Checkbox>
                {labelRender ? labelRender(_value, record, 0) : null}
              </Space>
            );
          }
        },
        {
          ...columns[1],
          title: 'Berigelse',
          key: 'workListEnrichment',
          width: 340,
          render: (_value: unknown, record: CompanySearchResult) => {
            const workListRecord = record as any;
            const draft = workListDraftValues[workListRecord.id] || {};
            return (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Input
                  placeholder="Hjemmeside"
                  size="small"
                  style={{ width: '100%' }}
                  value={draft.beriget_hjemmeside ?? workListRecord.beriget_hjemmeside ?? ''}
                  onChange={(e) =>
                    setWorkListDraftValues((prev) => ({
                      ...prev,
                      [workListRecord.id]: { ...(prev[workListRecord.id] || {}), beriget_hjemmeside: e.target.value }
                    }))
                  }
                  onBlur={(e) => {
                    const nextValue = e.target.value.trim();
                    const normalized = nextValue ? nextValue : null;
                    if ((workListRecord.beriget_hjemmeside ?? null) !== normalized) {
                      void handleWorkListItemEnrichment(workListRecord.id, { beriget_hjemmeside: normalized });
                    }
                  }}
                />
                <Input
                  placeholder="Kontakt"
                  size="small"
                  style={{ width: '100%' }}
                  value={draft.beriget_kontakt ?? workListRecord.beriget_kontakt ?? ''}
                  onChange={(e) =>
                    setWorkListDraftValues((prev) => ({
                      ...prev,
                      [workListRecord.id]: { ...(prev[workListRecord.id] || {}), beriget_kontakt: e.target.value }
                    }))
                  }
                  onBlur={(e) => {
                    const nextValue = e.target.value.trim();
                    const normalized = nextValue ? nextValue : null;
                    if ((workListRecord.beriget_kontakt ?? null) !== normalized) {
                      void handleWorkListItemEnrichment(workListRecord.id, { beriget_kontakt: normalized });
                    }
                  }}
                />
                <Input
                  placeholder="Telefon"
                  size="small"
                  style={{ width: '100%' }}
                  value={draft.beriget_telefon ?? workListRecord.beriget_telefon ?? ''}
                  onChange={(e) =>
                    setWorkListDraftValues((prev) => ({
                      ...prev,
                      [workListRecord.id]: { ...(prev[workListRecord.id] || {}), beriget_telefon: e.target.value }
                    }))
                  }
                  onBlur={(e) => {
                    const nextValue = e.target.value.trim();
                    const normalized = nextValue ? nextValue : null;
                    if ((workListRecord.beriget_telefon ?? null) !== normalized) {
                      void handleWorkListItemEnrichment(workListRecord.id, { beriget_telefon: normalized });
                    }
                  }}
                />
                <Input
                  placeholder="Email"
                  size="small"
                  style={{ width: '100%' }}
                  value={draft.beriget_email ?? workListRecord.beriget_email ?? ''}
                  onChange={(e) =>
                    setWorkListDraftValues((prev) => ({
                      ...prev,
                      [workListRecord.id]: { ...(prev[workListRecord.id] || {}), beriget_email: e.target.value }
                    }))
                  }
                  onBlur={(e) => {
                    const nextValue = e.target.value.trim();
                    const normalized = nextValue ? nextValue : null;
                    if ((workListRecord.beriget_email ?? null) !== normalized) {
                      void handleWorkListItemEnrichment(workListRecord.id, { beriget_email: normalized });
                    }
                  }}
                />
                <Input.TextArea
                  placeholder="Bemærkninger"
                  size="small"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ width: '100%' }}
                  value={draft.bemaerkninger ?? workListRecord.bemaerkninger ?? ''}
                  onChange={(e) =>
                    setWorkListDraftValues((prev) => ({
                      ...prev,
                      [workListRecord.id]: { ...(prev[workListRecord.id] || {}), bemaerkninger: e.target.value }
                    }))
                  }
                  onBlur={(e) => {
                    const nextValue = e.target.value.trim();
                    const normalized = nextValue ? nextValue : null;
                    if ((workListRecord.bemaerkninger ?? null) !== normalized) {
                      void handleWorkListItemEnrichment(workListRecord.id, { bemaerkninger: normalized });
                    }
                  }}
                />
              </Space>
            );
          }
        },
        {
          ...columns[4],
          title: 'Handlinger',
          key: 'actions',
          width: 220,
          fixed: 'right',
          render: (_value: unknown, record: CompanySearchResult, index: number) => {
            return actionsRender ? actionsRender(_value, record, index) : null;
          }
        }
      ];
    },
    [columns, handleWorkListItemEnrichment, handleWorkListItemUpdate, workListDraftValues, workListUpdatingFor]
  );

  const displayColumns = activeWorkList ? combinedWorkListColumns : columns;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {saveJob ? (
            <Alert
              type={
                saveJob.status === 'FAILED'
                  ? 'error'
                  : saveJob.status === 'COMPLETED'
                    ? 'success'
                    : 'info'
              }
              showIcon
              message={`Gem-job ${saveJob.status.toLowerCase()} · ${saveJob.progress}%`}
              description={
                saveJob.status === 'FAILED'
                  ? saveJob.errorMessage || 'Gem-job fejlede'
                  : `Nye: ${saveJob.insertedCount} · Opdaterede: ${saveJob.updatedCount} · I alt: ${saveJob.totalCount}`
              }
            />
          ) : null}
          {activeWorkList ? (
            <>
              <Space direction="vertical" size={2}>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Arbejdsliste: {activeWorkList.display_name}
                </Title>
                <Text type="secondary">{totalResults} rækker matcher de valgte filtre</Text>
              </Space>
              <Space wrap size={[8, 8]} align="center">
                <Input
                  allowClear
                  placeholder="Søg i listen (navn/CVR)"
                  style={{ width: 300, maxWidth: '100%' }}
                  value={workListFilterDraft.q}
                  onChange={(event) =>
                    setWorkListFilterDraft((current) => ({ ...current, q: event.target.value }))
                  }
                  onPressEnter={() => void handleWorkListToolbarSearch()}
                />
                <Select
                  aria-label="Rækkefilter"
                  style={{ width: 210, maxWidth: '100%' }}
                  value={workListFilterDraft.changeFilter}
                  onChange={(changeFilter: WorkListChangeFilter) =>
                    setWorkListFilterDraft((current) => ({ ...current, changeFilter }))
                  }
                  options={[
                    { value: 'ALL', label: 'Alle rækker' },
                    { value: 'NEW', label: 'Nye rækker' },
                    { value: 'UPDATED', label: 'Opdaterede rækker' },
                    { value: 'CHANGED', label: 'Alle ændrede rækker' }
                  ]}
                />
                <Select
                  aria-label="Email sendt"
                  style={{ width: 210, maxWidth: '100%' }}
                  value={workListFilterDraft.emailSent}
                  onChange={(emailSent: WorkListBooleanFilter) =>
                    setWorkListFilterDraft((current) => ({ ...current, emailSent }))
                  }
                  options={[
                    { value: 'ALL', label: 'Email sendt: Alle' },
                    { value: 'YES', label: 'Email sendt: Ja' },
                    { value: 'NO', label: 'Email sendt: Nej' }
                  ]}
                />
                <Select
                  aria-label="Allerede kunde"
                  style={{ width: 220, maxWidth: '100%' }}
                  value={workListFilterDraft.alreadyCustomer}
                  onChange={(alreadyCustomer: WorkListBooleanFilter) =>
                    setWorkListFilterDraft((current) => ({ ...current, alreadyCustomer }))
                  }
                  options={[
                    { value: 'ALL', label: 'Allerede kunde: Alle' },
                    { value: 'YES', label: 'Allerede kunde: Ja' },
                    { value: 'NO', label: 'Allerede kunde: Nej' }
                  ]}
                />
                <Select
                  aria-label="Ignorer"
                  style={{ width: 190, maxWidth: '100%' }}
                  value={workListFilterDraft.ignored}
                  onChange={(ignored: WorkListBooleanFilter) =>
                    setWorkListFilterDraft((current) => ({ ...current, ignored }))
                  }
                  options={[
                    { value: 'ALL', label: 'Ignorer: Alle' },
                    { value: 'YES', label: 'Ignorer: Ja' },
                    { value: 'NO', label: 'Ignorer: Nej' }
                  ]}
                />
                <Button type="primary" onClick={() => void handleWorkListToolbarSearch()} loading={loading}>
                  Søg
                </Button>
                <Button onClick={handleCloseWorkList}>Luk liste</Button>
                <Button onClick={() => void handleRefreshWorkList()} loading={workListRefreshing}>
                  Opdater liste
                </Button>
                <Button onClick={() => void handleExportWorkList()} loading={workListExporting}>
                  Gem til Excel
                </Button>
              </Space>
            </>
          ) : (
            <>
              <Row gutter={[16, 12]} align="top">
                <Col xs={24} xl={13}>
                  <Space direction="vertical" size={4}>
                    <Title level={4} style={{ marginBottom: 0 }}>
                      Søg i virksomheder
                    </Title>
                    <Text type="secondary">
                      Udfyld et eller flere felter, eller åbn en gemt arbejdsliste med samme søgeoverblik.
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} xl={11}>
                  <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Select
                      allowClear
                      showSearch
                      loading={workListsLoading}
                      placeholder="Åbn gemt arbejdsliste"
                      optionFilterProp="label"
                      style={{ minWidth: 280 }}
                      onChange={(value) => {
                        if (value) {
                          void openWorkList(value);
                        }
                      }}
                      options={workLists.map((workList) => ({
                        value: workList.id,
                        label: `${workList.display_name} (${workList.item_count})`
                      }))}
                    />
                    <Button onClick={() => void loadWorkLists()} loading={workListsLoading}>
                      Opdater lister
                    </Button>
                  </Space>
                </Col>
              </Row>
              <Form
                layout="vertical"
                form={form}
                onFinish={(values) => void performSearch(values)}
                disabled={loading}
                initialValues={{ includeExistingLeads: false, saveAsList: false, limit: DEFAULT_PAGE_SIZE }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="name" label="Virksomhedsnavn">
                      <Input placeholder="F.eks. Acme A/S" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="cvr" label="CVR-nummer">
                      <Input placeholder="F.eks. 12345678" allowClear maxLength={20} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="status" label="Status">
                      <Input placeholder="F.eks. Aktiv" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="branche" label="Branche">
                      <Select
                        allowClear
                        showSearch
                        placeholder="Vælg branche"
                        loading={brancheLoading}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label as string)
                            ?.toLocaleLowerCase()
                            .includes(input.toLocaleLowerCase()) ?? false
                        }
                        options={brancheOptions.map((option) => ({
                          value: option.code || option.text,
                          label: option.code ? `${option.text} (${option.code})` : option.text
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="postal" label="Postnr./by">
                      <Input placeholder="F.eks. 2100 eller København" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="municipality" label="Kommune">
                      <Input placeholder="F.eks. København" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="minEmployees" label="Min. antal ansatte">
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="F.eks. 10" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item name="maxEmployees" label="Max. antal ansatte">
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="F.eks. 250" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      name="limit"
                      label="Resultater pr. søgning"
                      tooltip="Vælg hvor mange virksomheder der vises pr. søgning."
                      rules={[{ type: 'number', min: 1 }]}
                    >
                      <InputNumber min={1} max={MAX_PAGE_SIZE} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="includeExistingLeads" valuePropName="checked" style={{ marginBottom: 16 }}>
                  <Checkbox>Medtag virksomheder som allerede er oprettet som leads</Checkbox>
                </Form.Item>
                <Form.Item name="saveAsList" valuePropName="checked" style={{ marginBottom: 16 }}>
                  <Checkbox>Gem søgeresultat som arbejdsliste</Checkbox>
                </Form.Item>
                {saveAsListChecked ? (
                <Form.Item
                  name="workListName"
                  label="Listenavn"
                  rules={[{ required: true, message: 'Angiv et listenavn' }]}
                  style={{ marginBottom: 16 }}
                >
                  <Input placeholder="F.eks. Nordsjælland - uge 29" maxLength={120} />
                </Form.Item>
                ) : null}
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Søg
                  </Button>
                  <Button onClick={handleReset}>Nulstil</Button>
                </Space>
              </Form>
            </>
          )}
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="cvrnummer"
          columns={displayColumns}
          dataSource={results}
          loading={loading}
          scroll={{ x: activeWorkList ? 1060 : 1500 }}
          locale={{ emptyText: loading ? <Spin /> : <Empty description="Ingen resultater" /> }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            hideOnSinglePage: false,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} af ${total}`
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title="Lead-detaljer"
        open={leadDetailVisible}
        onCancel={closeLeadDetail}
        footer={null}
        width={960}
        destroyOnClose
      >
        {leadDetailId ? (
          <LeadDetailPage
            key={leadDetailId}
            canManage={canManage}
            onBack={handleLeadDetailBack}
            onLeadUpdated={handleLeadUpdated}
            leadIdOverride={leadDetailId}
            locationStateOverride={leadDetailLocationState}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        )}
      </Modal>

      <Modal
        title={detail ? detail.navn || formatCvrNumber(detailCvr || '') : 'Virksomhedsdetaljer'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width="min(960px, calc(100vw - 32px))"
        destroyOnClose
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {detailLoading ? <Text type="secondary">Henter virksomhedsdetaljer...</Text> : null}
          {detail ? (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="CVR">{formatCvrNumber(detail.cvrnummer || detailCvr || '')}</Descriptions.Item>
              <Descriptions.Item label="Status">{detail.virksomhed_status || 'Ukendt'}</Descriptions.Item>
              <Descriptions.Item label="Branchekode">{detail.branchekode || '—'}</Descriptions.Item>
              <Descriptions.Item label="Branchetekst">{detail.branchetekst || '—'}</Descriptions.Item>
              <Descriptions.Item label="Ansatte (seneste år)">
                {formatEmployeeCountLabel(detail.ansatte_aar)}
              </Descriptions.Item>
              <Descriptions.Item label="Ansatte opdateret til">
                {formatDateOnly(detail.ansatte_datotil)}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse">{buildAddress(detail) || '—'}</Descriptions.Item>
              <Descriptions.Item label="Kommune">{detail.kommunenavn || '—'}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{detail.telefon || '—'}</Descriptions.Item>
              <Descriptions.Item label="E-mail">{detail.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Opdateret">{formatUpdated(detail.opdateret_tidspunkt)}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty description="Ingen CVR-data" />
          )}

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={5} style={{ marginBottom: 0 }}>DAR-adresser</Title>
            {propertyDetailLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : propertyDetailError ? (
              <Alert type="warning" showIcon message={propertyDetailError} />
            ) : propertyDetail?.darAddresses?.length ? (
              <List
                bordered
                dataSource={propertyDetail.darAddresses.filter((address, index, addresses) => {
                  const key = address.displayAddress || address.id || '';
                  return addresses.findIndex((candidate) => (candidate.displayAddress || candidate.id || '') === key) === index;
                })}
                renderItem={(address) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>{address.displayAddress || 'Adresse uden visningsnavn'}</Text>
                      <Space size={6} wrap>
                        {address.status ? <Tag color={address.status === '3' ? 'green' : 'default'}>{address.status === '3' ? 'Gældende' : `Status ${address.status}`}</Tag> : null}
                        {address.streetName ? <Tag>{address.streetName}</Tag> : null}
                        {address.postalCode || address.city ? <Tag>{[address.postalCode, address.city].filter(Boolean).join(' ')}</Tag> : null}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Ingen tilknyttede DAR-adresser fundet" />
            )}

            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Title level={5} style={{ marginBottom: 0 }}>BBR-objekter</Title>
              {propertyDetail?.latestSourceUpdatedAt ? (
                <Text type="secondary">Seneste BBR-registeropdatering: {formatUpdated(propertyDetail.latestSourceUpdatedAt)}</Text>
              ) : null}
            </Space>
            {propertyDetailLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : propertyDetailError ? null : propertyDetail?.bbrObjects?.length ? (
              <List
                bordered
                dataSource={propertyDetail.bbrObjects}
                renderItem={(item) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={6} wrap>
                        <Text strong>{item.buildingNumber ? `Bygning ${item.buildingNumber}` : item.type || 'BBR-objekt'}</Text>
                        {item.statusLabel ? <Tag color={item.status === '6' ? 'green' : 'default'}>{item.statusLabel}</Tag> : null}
                        {item.usage ? <Tag color="processing">{item.usage}</Tag> : null}
                      </Space>
                      <Text type="secondary">{item.displayAddress || 'Adresse ikke angivet'}</Text>
                      <Space size={6} wrap>
                        {item.sourceUpdatedAt ? <Tag color="blue">Opdateret {formatUpdated(item.sourceUpdatedAt)}</Tag> : null}
                        {item.validFrom ? <Tag>Gældende fra {formatDateOnly(item.validFrom)}</Tag> : null}
                        {item.registrationFrom ? <Tag>Registreret {formatUpdated(item.registrationFrom)}</Tag> : null}
                        {item.businessEvent ? <Tag>Hændelse: {item.businessEvent}</Tag> : null}
                        {typeof item.totalArea === 'number' && Number.isFinite(item.totalArea) ? <Tag>Samlet areal {item.totalArea} m²</Tag> : null}
                        {typeof item.builtArea === 'number' && Number.isFinite(item.builtArea) ? <Tag>Bebygget {item.builtArea} m²</Tag> : null}
                        {typeof item.residentialArea === 'number' && Number.isFinite(item.residentialArea) ? <Tag>Bolig {item.residentialArea} m²</Tag> : null}
                        {typeof item.commercialArea === 'number' && Number.isFinite(item.commercialArea) ? <Tag>Erhverv {item.commercialArea} m²</Tag> : null}
                        {typeof item.integratedGarageArea === 'number' && Number.isFinite(item.integratedGarageArea) ? <Tag>Indbygget garage {item.integratedGarageArea} m²</Tag> : null}
                        {typeof item.integratedCarportArea === 'number' && Number.isFinite(item.integratedCarportArea) ? <Tag>Indbygget carport {item.integratedCarportArea} m²</Tag> : null}
                        {typeof item.integratedOutbuildingArea === 'number' && Number.isFinite(item.integratedOutbuildingArea) ? <Tag>Indbygget udhus {item.integratedOutbuildingArea} m²</Tag> : null}
                        {typeof item.integratedConservatoryArea === 'number' && Number.isFinite(item.integratedConservatoryArea) ? <Tag>Indbygget udestue {item.integratedConservatoryArea} m²</Tag> : null}
                        {typeof item.buildYear === 'number' && Number.isFinite(item.buildYear) ? <Tag>Opført {item.buildYear}</Tag> : null}
                        {typeof item.rebuildYear === 'number' && Number.isFinite(item.rebuildYear) ? <Tag>Om-/tilbygget {item.rebuildYear}</Tag> : null}
                        {typeof item.floors === 'number' && Number.isFinite(item.floors) ? <Tag>Etager {item.floors}</Tag> : null}
                        {item.outerWallMaterial ? <Tag>Ydervæg: {item.outerWallMaterial}</Tag> : null}
                        {item.supplementaryOuterWallMaterial ? <Tag>Suppl. ydervæg: {item.supplementaryOuterWallMaterial}</Tag> : null}
                        {item.roofMaterial ? <Tag>Tag: {item.roofMaterial}</Tag> : null}
                        {item.supplementaryRoofMaterial ? <Tag>Suppl. tag: {item.supplementaryRoofMaterial}</Tag> : null}
                        {item.waterSupply ? <Tag>Vand: {item.waterSupply}</Tag> : null}
                        {item.heatingInstallation ? <Tag>Varme: {item.heatingInstallation}</Tag> : null}
                        {item.heatingFuel ? <Tag>Opvarmning: {item.heatingFuel}</Tag> : null}
                        {item.supplementaryHeating ? <Tag>Suppl. varme: {item.supplementaryHeating}</Tag> : null}
                        {item.asbestosMaterial ? <Tag>Asbest: {item.asbestosMaterial}</Tag> : null}
                        {item.materialSource ? <Tag>Kilde: {item.materialSource}</Tag> : null}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Ingen tilknyttede BBR-objekter fundet" />
            )}

            <Title level={5} style={{ marginBottom: 0 }}>Byggesager og bygningsændringer</Title>
            {propertyDetail?.buildingCases?.length ? (
              <List
                bordered
                dataSource={propertyDetail.buildingCases}
                renderItem={(buildingCase) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={6} wrap>
                        <Tag color="orange">BBR-sag</Tag>
                        <Text strong>{buildingCase.caseCodeLabel || buildingCase.caseType || 'Byggesag'}</Text>
                        {buildingCase.caseNumber ? <Tag>Sag {buildingCase.caseNumber}</Tag> : null}
                        {buildingCase.status ? <Tag>Status {buildingCase.status}</Tag> : null}
                      </Space>
                      {buildingCase.address ? <Text>{buildingCase.address}</Text> : null}
                      <Space size={6} wrap>
                        {buildingCase.caseType ? <Tag>{buildingCase.caseType}</Tag> : null}
                        {buildingCase.levelType ? <Tag>{buildingCase.levelType}</Tag> : null}
                        {buildingCase.buildingNumber ? <Tag>Bygning {buildingCase.buildingNumber}</Tag> : null}
                        {buildingCase.buildingUsage ? <Tag>{buildingCase.buildingUsage}</Tag> : null}
                        {buildingCase.caseDate ? <Tag>Sagsdato {formatDateOnly(buildingCase.caseDate)}</Tag> : null}
                        {buildingCase.applicationReceivedDate ? <Tag>Ansøgning {formatDateOnly(buildingCase.applicationReceivedDate)}</Tag> : null}
                        {buildingCase.permitDate ? <Tag>Tilladelse {formatDateOnly(buildingCase.permitDate)}</Tag> : null}
                        {buildingCase.startDate ? <Tag>Påbegyndt {formatDateOnly(buildingCase.startDate)}</Tag> : null}
                        {buildingCase.occupancyPermitDate ? <Tag>Ibrugtagning {formatDateOnly(buildingCase.occupancyPermitDate)}</Tag> : null}
                        {buildingCase.completionDate ? <Tag>Fuldført {formatDateOnly(buildingCase.completionDate)}</Tag> : null}
                        {buildingCase.sourceUpdatedAt ? <Tag>Opdateret {formatUpdated(buildingCase.sourceUpdatedAt)}</Tag> : null}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : insuranceEventsLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : insuranceEventsError ? (
              <Alert type="warning" showIcon message={insuranceEventsError} />
            ) : insuranceEvents?.events?.some(isConstructionRelatedInsuranceEvent) ? (
              <List
                bordered
                dataSource={insuranceEvents.events.filter(isConstructionRelatedInsuranceEvent)}
                renderItem={(event) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={6} wrap>
                        <Tag color={insuranceSeverityColor(event.severity)}>{insuranceSeverityLabel(event.severity)}</Tag>
                        <Text strong>{event.title}</Text>
                        {event.sourceRegisters.map((register) => <Tag key={register}>{register}</Tag>)}
                      </Space>
                      {event.description ? <Text>{event.description}</Text> : null}
                      <Space size={6} wrap>
                        <Tag>Status: {event.status === 'OPEN' ? 'Åben' : event.status}</Tag>
                        <Tag>Registreret {formatUpdated(event.detectedAt)}</Tag>
                        {event.sourceObjectId ? <Tag>{event.sourceObjectId}</Tag> : null}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Ingen byggesager eller bygningsændringer registreret endnu" />
            )}

            <Title level={5} style={{ marginBottom: 0 }}>Forsikringssignaler</Title>
            {insuranceEventsLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : insuranceEventsError ? (
              <Alert type="warning" showIcon message={insuranceEventsError} />
            ) : insuranceEvents?.events?.some((event) => !isConstructionRelatedInsuranceEvent(event)) ? (
              <List
                bordered
                dataSource={insuranceEvents.events.filter((event) => !isConstructionRelatedInsuranceEvent(event))}
                renderItem={(event) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={6} wrap>
                        <Tag color={insuranceSeverityColor(event.severity)}>{insuranceSeverityLabel(event.severity)}</Tag>
                        <Text strong>{event.title}</Text>
                        {event.sourceRegisters.map((register) => <Tag key={register}>{register}</Tag>)}
                      </Space>
                      {event.description ? <Text>{event.description}</Text> : null}
                      <Space size={6} wrap>
                        <Tag>Status: {event.status === 'OPEN' ? 'Åben' : event.status}</Tag>
                        <Tag>Registreret {formatUpdated(event.detectedAt)}</Tag>
                        {event.sourceObjectId ? <Tag>{event.sourceObjectId}</Tag> : null}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Ingen forsikringsrelevante registersignaler endnu" />
            )}

            <Title level={5} style={{ marginBottom: 0 }}>Seneste registerændringer</Title>
            {propertyChangesLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : propertyChangesError ? (
              <Alert type="warning" showIcon message={propertyChangesError} />
            ) : propertyChanges?.events?.length ? (
              <List
                bordered
                dataSource={propertyChanges.events}
                renderItem={(event) => {
                  const changedFieldCount = Object.keys(event.changedFields || {}).length;
                  return (
                    <List.Item>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space size={6} wrap>
                          <Tag color={event.registerType === 'BBR' ? 'blue' : 'cyan'}>{event.registerType}</Tag>
                          <Tag color={propertyChangeColor(event.changeType)}>{propertyChangeLabel(event.changeType)}</Tag>
                          <Text strong>{event.objectId}</Text>
                        </Space>
                        <Space size={6} wrap>
                          <Tag>Registreret {formatUpdated(event.detectedAt)}</Tag>
                          {event.newSourceUpdatedAt ? <Tag>Kilde opdateret {formatUpdated(event.newSourceUpdatedAt)}</Tag> : null}
                          {changedFieldCount ? <Tag>{changedFieldCount} ændrede felter</Tag> : null}
                        </Space>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty description="Ingen registrerede DAR/BBR-ændringer endnu" />
            )}
          </Space>
        </Space>
      </Modal>

      <Modal
        title={`AI-analyse ${formatCvrNumber(aiSummary?.cvrnummer || aiSummaryCvr || '')}`}
        open={aiModalVisible}
        onCancel={() => {
          setAiModalVisible(false);
          setAiSummary(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {aiModalLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : aiSummary?.ai_summary_text ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space direction="vertical" size={4}>
              {aiSummary.ai_summary_model ? (
                <Text type="secondary">Model: {aiSummary.ai_summary_model}</Text>
              ) : null}
              {aiModalGeneratedLabel ? (
                <Text type="secondary">Senest genereret {aiModalGeneratedLabel}</Text>
              ) : null}
            </Space>
            {aiAddressContacts.length > 0 ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>Mulige administrationskontakter (samme adresse)</Text>
                {aiAddressContacts.map((candidate, index) => (
                  <Card
                    key={`${candidate.cvrnummer || 'ukendt'}-${candidate.email || candidate.telefon || index}`}
                    size="small"
                    styles={{ body: { padding: 12 } }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>
                        {candidate.navn || 'Ukendt navn'}
                        {candidate.cvrnummer ? ` (CVR ${formatCvrNumber(candidate.cvrnummer)})` : ''}
                      </Text>
                      <Text type="secondary">
                        {candidate.status || 'Ukendt status'}
                        {candidate.branchetekst ? ` · ${candidate.branchetekst}` : ''}
                      </Text>
                      <Space size={8} wrap>
                        <Tag color={candidate.email ? 'blue' : 'default'}>
                          E-mail: {candidate.email || 'Ukendt'}
                        </Tag>
                        <Tag color={candidate.telefon ? 'geekblue' : 'default'}>
                          Telefon: {candidate.telefon || 'Ukendt'}
                        </Tag>
                      </Space>
                    </Space>
                  </Card>
                ))}
              </Space>
            ) : null}
            {aiModalSections.length > 0 ? (
              aiModalSections.map((section: string, index: number) => (
                <Paragraph
                  key={`ai-modal-section-${index}`}
                  style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                >
                  {section}
                </Paragraph>
              ))
            ) : (
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {aiSummary.ai_summary_text.trim()}
              </Paragraph>
            )}
          </Space>
        ) : (
          <Empty description="Ingen AI-data" />
        )}
      </Modal>

      <Modal
        title={`Kontaktoplysninger ${formatCvrNumber(contactLookup?.cvrnummer || contactLookupCvr || '')}`}
        open={contactModalVisible}
        onCancel={() => {
          setContactModalVisible(false);
          setContactLookup(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {contactModalLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : contactLookup ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Virksomhed">{contactLookup.company_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Adresse">{contactLookup.address || '—'}</Descriptions.Item>
              <Descriptions.Item label="Primær e-mail">{contactLookup.company_email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Primær telefon">{contactLookup.company_phone || '—'}</Descriptions.Item>
            </Descriptions>
            {Array.isArray(contactLookup.address_contact_candidates) &&
            contactLookup.address_contact_candidates.length > 0 ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>Mulige administrationskontakter (samme adresse)</Text>
                {contactLookup.address_contact_candidates.map((candidate, index) => (
                  <Card
                    key={`${candidate.cvrnummer || 'ukendt'}-${candidate.email || candidate.telefon || index}`}
                    size="small"
                    styles={{ body: { padding: 12 } }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>
                        {candidate.navn || 'Ukendt navn'}
                        {candidate.cvrnummer ? ` (CVR ${formatCvrNumber(candidate.cvrnummer)})` : ''}
                      </Text>
                      <Text type="secondary">
                        {candidate.status || 'Ukendt status'}
                        {candidate.branchetekst ? ` · ${candidate.branchetekst}` : ''}
                      </Text>
                      <Space size={8} wrap>
                        <Tag color={candidate.email ? 'blue' : 'default'}>
                          E-mail: {candidate.email || 'Ukendt'}
                        </Tag>
                        <Tag color={candidate.telefon ? 'geekblue' : 'default'}>
                          Telefon: {candidate.telefon || 'Ukendt'}
                        </Tag>
                      </Space>
                    </Space>
                  </Card>
                ))}
              </Space>
            ) : (
              <Empty description="Ingen supplerende kontaktoplysninger fundet" />
            )}
          </Space>
        ) : (
          <Empty description="Ingen kontaktoplysninger" />
        )}
      </Modal>

      <Modal
        title={
          usersCompany
            ? `Brugere for ${usersCompany.name || formatCvrNumber(usersCompany.cvr)}`
            : 'Brugere'
        }
        open={usersModalOpen}
        onCancel={closeUsersModal}
        footer={null}
        width={680}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Table
            size="small"
            rowKey={(row) => row.id}
            dataSource={companyUsers}
            loading={usersLoading}
            pagination={false}
            locale={{ emptyText: usersLoading ? <Spin /> : <Empty description="Ingen brugere" /> }}
            columns={[
              {
                title: 'Navn',
                dataIndex: 'full_name',
                key: 'full_name',
                render: (value: string | null) => value || '—'
              },
              {
                title: 'E-mail',
                dataIndex: 'email',
                key: 'email',
                render: (value: string | null) => value || '—'
              },
              {
                title: 'Rolle',
                dataIndex: 'role',
                key: 'role',
                render: (value: string | null) => value || '—'
              }
            ]}
          />

          <Card size="small" title="Opret bruger" bordered>
            <Form form={userForm} layout="vertical" preserve={false} onFinish={handleCreateUser}>
              <Form.Item name="full_name" label="Navn" rules={[{ required: true, message: 'Angiv navn' }]}>
                <Input placeholder="F.eks. Maria Hansen" />
              </Form.Item>
              <Form.Item
                name="email"
                label="E-mail"
                rules={[{ required: true, type: 'email', message: 'Angiv gyldig e-mail' }]}
              >
                <Input placeholder="bruger@firma.dk" />
              </Form.Item>
              <Form.Item name="role" label="Rolle" initialValue="REFERRER">
                <Select
                  options={[
                    { value: 'REFERRER', label: 'Referrer' },
                    { value: 'BROKER_ADMIN', label: 'Administrator' }
                  ]}
                />
              </Form.Item>
              <Space>
                <Button onClick={() => userForm.resetFields()}>Nulstil</Button>
                <Button type="primary" htmlType="submit" loading={createUserLoading} disabled={!currentUsersCompanyId}>
                  Opret bruger
                </Button>
              </Space>
            </Form>
          </Card>
        </Space>
      </Modal>
    </Space>
  );
}
